import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import { 
  Home, CheckSquare, Plus, Search, User, Filter, ArrowUpDown, LayoutGrid, 
  Bell, HelpCircle, Settings, ChevronDown, MessageSquare, Copy, Download, Archive, Trash2, 
  RefreshCw, ArrowRight, Box, X, Paperclip, Image as ImageIcon, Smile, Clock, Sparkles, Send, 
  UserPlus, PlusCircle, FileText, ChevronRight, 
  FolderOpen, Check, Pencil,
  Users, LogOut, Wifi, WifiOff, AlertTriangle, CheckCircle, Eye, EyeOff, Share2, ExternalLink,
  CalendarRange, Bot
} from 'lucide-react';
import * as api from './api';
import { AppStateContext } from './contexts/AppStateContext';
import compressImage from './utils/compressImage';
import buildInfo from './build-info.json';

// --- EXTRACTED MODULES ---
import { INITIAL_STATUS_COLORS, ALBUM_STATUS_COLORS, GROUP_COLORS, INITIAL_CATEGORIES, INITIAL_GROUPS, INITIAL_COLUMNS, INITIAL_TASKS, INITIAL_ALBUMS, INITIAL_THEME, INITIAL_TASK_STATUS_CONFIG } from './constants';
import { useToasts } from './hooks/useToasts';
import { useColumns } from './hooks/useColumns';
import { useStatusEditor } from './hooks/useStatusEditor';
import { useTaskActions } from './hooks/useTaskActions';
import { useGroupActions } from './hooks/useGroupActions';
import { useSync } from './hooks/useSync';
import { DEFAULT_NOTIFICATION_SETTINGS, createDefaultUserNotificationPrefs, normalizeUserNotificationPrefs, isNowInQuietHours } from './utils/notificationPreferences';
import UpdatesInputBox from './components/UpdatesInputBox';
import ToolbarButton from './components/ToolbarButton';
import KanbanBoard from './components/KanbanBoard';
import CalendarBoard from './components/CalendarBoard';
import ColumnMenuDropdown from './components/ColumnMenuDropdown';
import SlaReportModal from './components/SlaReportModal';
import DashboardSummary from './components/DashboardSummary';
import TaskCellContent from './components/TaskCellContent';
import EditableTextInput from './components/EditableTextInput';
import FotoAiWidget from './components/FotoAiWidget';
import LoginScreen from './pages/LoginScreen';
import LegalDocumentScreen from './pages/LegalDocumentScreen';
const HomeDashboard = lazy(() => import('./pages/HomeDashboard'));
const MyWorkScreen = lazy(() => import('./pages/MyWorkScreen'));
const AlbumsScreen = lazy(() => import('./pages/AlbumsScreen'));
const TeamManagementScreen = lazy(() => import('./pages/TeamManagementScreen'));
const SettingsScreen = lazy(() => import('./pages/SettingsScreen'));
const NotificationsCenterScreen = lazy(() => import('./pages/NotificationsCenterScreen'));
const MessageHubScreen = lazy(() => import('./pages/MessageHubScreen'));
const SalesAgentScreen = lazy(() => import('./pages/SalesAgentScreen'));
const ClientsScreen = lazy(() => import('./pages/ClientsScreen'));
const ClientDetailScreen = lazy(() => import('./pages/ClientDetailScreen'));
const AlbumReviewPortal = lazy(() => import('./pages/AlbumReviewPortal'));
const SocialPostScreen = lazy(() => import('./pages/SocialPostScreen'));
const EquipamentosLocacaoScreen = lazy(() => import('./pages/EquipamentosLocacaoScreen'));
const EscalaFreelancePortal = lazy(() => import('./pages/EscalaFreelancePortal'));


class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { hasError: false, error: null }; }
  static getDerivedStateFromError(error) { return { hasError: true, error }; }
  componentDidCatch(error, info) {
    console.error('🔴 ErrorBoundary caught:', error, info);
    console.error('🔴 Error stack:', error?.stack);
    console.error('🔴 Component stack:', info?.componentStack);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen items-center justify-center bg-[#181b26] text-white">
          <div className="text-center p-8">
            <AlertTriangle size={48} className="text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold mb-2">Algo deu errado</h2>
            <p className="text-slate-400 mb-4">{this.state.error?.message || 'Erro inesperado'}</p>
            <button onClick={() => { this.setState({ hasError: false, error: null }); window.location.reload(); }} className="px-4 py-2 bg-indigo-600 rounded hover:bg-indigo-700 transition-colors">Recarregar</button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [currentUser, setCurrentUser] = useState(null);
  
  // ============================================
  // TIMEZONE BRASIL - Usa hora do servidor
  // ============================================
  const [serverTimeOffset, setServerTimeOffset] = useState(null); // diff em ms entre servidor e dispositivo
  
  useEffect(() => {
    api.fetchServerTime().then(st => {
      const serverMs = new Date(st.iso).getTime();
      const localMs = Date.now();
      setServerTimeOffset(serverMs - localMs);
    }).catch(() => {
      // Fallback: usar offset do timezone do Brasil
      const localNow = new Date();
      const brlNow = new Date(localNow.toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
      setServerTimeOffset(brlNow.getTime() - localNow.getTime());
    });
  }, []);

  // Retorna Date ajustado para timezone do Brasil
  const getBrazilDate = useCallback(() => {
    if (serverTimeOffset !== null) {
      return new Date(Date.now() + serverTimeOffset);
    }
    return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }));
  }, [serverTimeOffset]);

  // Retorna data no formato YYYY-MM-DD (Brasil)
  const getBrazilDateStr = useCallback(() => {
    const d = getBrazilDate();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }, [getBrazilDate]);

  // Formatar tamanho de arquivo
  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const units = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
  };

  const [confirmDialog, setConfirmDialog] = useState(null);

  const [albums, setAlbums] = useState(INITIAL_ALBUMS);
  const [theme, setTheme] = useState(INITIAL_THEME);
  const [members, setMembers] = useState([]);
  const [tasks, setTasks] = useState(INITIAL_TASKS);
  const [groups, setGroups] = useState(INITIAL_GROUPS);
  const [categories, setCategories] = useState(INITIAL_CATEGORIES);
  const [collapsedGroups, setCollapsedGroups] = useState([]);
  const [collapsedCategories, setCollapsedCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('quadro');
  const [selectedTasks, setSelectedTasks] = useState([]);
  
  const [activePanelTaskId, setActivePanelTaskId] = useState(null);
  const [showSlaReport, setShowSlaReport] = useState(false);
  const [activePanelTab, setActivePanelTab] = useState('atualizações');
  const [activeColorPickerGroupId, setActiveColorPickerGroupId] = useState(null);
  const [aiModel, setAiModel] = useState(localStorage.getItem('pf_ai_model') || 'deepseek-chat');
  const [serverBuildInfo, setServerBuildInfo] = useState(null);
  const [showBuildInfoModal, setShowBuildInfoModal] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [editingUpdateId, setEditingUpdateId] = useState(null);
  const [editingUpdateText, setEditingUpdateText] = useState('');
  const [lightboxImage, setLightboxImage] = useState(null);
  const [whatsAppShareModal, setWhatsAppShareModal] = useState(null);
  const [whatsAppShareTarget, setWhatsAppShareTarget] = useState('client');
  const [whatsAppShareMember, setWhatsAppShareMember] = useState('');
  const [isSendingWhatsAppNote, setIsSendingWhatsAppNote] = useState(false);
  const [whatsAppNotifyModal, setWhatsAppNotifyModal] = useState(null);
  const [whatsAppNotifyPriority, setWhatsAppNotifyPriority] = useState('ALTA');
  const [whatsAppNotifyObservation, setWhatsAppNotifyObservation] = useState('');
  const [isSendingWhatsAppNotify, setIsSendingWhatsAppNotify] = useState(false);

  const [statusOptions, setStatusOptions] = useState(INITIAL_STATUS_COLORS);
  const [albumStatusOptions, setAlbumStatusOptions] = useState(ALBUM_STATUS_COLORS);
  const [taskStatusConfig, setTaskStatusConfig] = useState(INITIAL_TASK_STATUS_CONFIG);
  const saveStateRef = useRef(null);
  const enqueueSaveRef = useRef(null);
  const {
    columns, setColumns,
    hiddenColumns, setHiddenColumns,
    colMenuOpen, setColMenuOpen,
    setResizingCol,
    addColMenuOpen, setAddColMenuOpen,
    handleColDragStart, handleColDrop,
    handleAddNewColumn, handleAddColumnRight, handleDuplicateColumn, handleChangeColumnType, handleRenameColumn, handleDeleteColumn,
    toggleColumnVisibility, showAllColumns, unhideColumn,
  } = useColumns(saveStateRef);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [editingColId, setEditingColId] = useState(null);
  const [editingColMenuKey, setEditingColMenuKey] = useState(null);
  const [editingColTitle, setEditingColTitle] = useState('');

  const [personFilter, setPersonFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [sortConfig, setSortConfig] = useState({ field: null, order: 'asc' });

  const [isPersonMenuOpen, setIsPersonMenuOpen] = useState(false);
  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [isSortMenuOpen, setIsSortMenuOpen] = useState(false);
  const [currentRoute, setCurrentRoute] = useState('gestao_photofine');
  const [clientDetailId, setClientDetailId] = useState(null);

  useEffect(() => {
    const adminOnlyRoutes = ['notification_center', 'settings', 'message_hub', 'sales_agent', 'client_detail', 'locacoes'];
    if (currentUser?.role !== 'admin' && adminOnlyRoutes.includes(currentRoute)) {
      setCurrentRoute('gestao_photofine');
    }
  }, [currentUser, currentRoute]);

  // Busca info do build do backend uma unica vez apos login
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    fetch((typeof window !== 'undefined' ? window.location.origin : '') + '/api/build-info')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data) setServerBuildInfo(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [currentUser]);

  // Estados para o modal de logs
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsFilterPerson, setLogsFilterPerson] = useState(null);
  const [logsFilterDateStart, setLogsFilterDateStart] = useState('');
  const [logsFilterDateEnd, setLogsFilterDateEnd] = useState('');
  const [logsFilterSearch, setLogsFilterSearch] = useState('');

  // Estados de notificação
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState(DEFAULT_NOTIFICATION_SETTINGS);
  // Intent de "abrir modal de revisão do álbum" disparado por cliques em notificações.
  // Cada novo intent tem um nonce único para que o AlbumsScreen reaja uma única vez.
  const [pendingReviewOpen, setPendingReviewOpen] = useState(null);
  const notificationDedupRef = useRef({});
  const { toasts, addToast, setToasts } = useToasts();

  // Definir datas do dia atual quando abrir o modal de logs
  useEffect(() => {
    if (isLogsModalOpen) {
      const today = getBrazilDateStr();
      setLogsFilterDateStart(today);
      setLogsFilterDateEnd(today);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLogsModalOpen]);

  // Função de persistência — salva local + enfileira para servidor via useSync
  const saveState = (newState) => {
    try {
      const existingData = localStorage.getItem('photofine_data');
      const currentData = existingData ? JSON.parse(existingData) : {};
      const dataToSave = { ...currentData, ...newState, updatedAt: getBrazilDate().toISOString() };
      localStorage.setItem('photofine_data', JSON.stringify(dataToSave));
    } catch (error) {
      console.error('Erro ao salvar:', error);
    }
    // Enqueue server save (handles offline queue internally)
    if (enqueueSaveRef.current) enqueueSaveRef.current(newState);
  };
  
  // Alias para compatibilidade
  const saveStateToLocalStorage = saveState;
  saveStateRef.current = saveStateToLocalStorage;

  // Memoize setters object to prevent infinite loop in useSync
  const setters = useMemo(() => ({
    setTasks, setGroups, setCategories, setStatusOptions, setColumns, setMembers, setTheme,
    setAlbums, setAlbumStatusOptions, setHiddenColumns, setNotifications, setUnreadCount, setNotificationSettings,
    saveStateRef,
  }), [
    setTasks,
    setGroups,
    setCategories,
    setStatusOptions,
    setColumns,
    setMembers,
    setTheme,
    setAlbums,
    setAlbumStatusOptions,
    setHiddenColumns,
    setNotifications,
    setUnreadCount,
    setNotificationSettings
  ]);
  const { isOnline, isSyncing, enqueueSave } = useSync(currentUser, setters, getBrazilDate);
  enqueueSaveRef.current = enqueueSave;

  const {
    isEditStatusModalOpen, setIsEditStatusModalOpen,
    editingStatuses, newStatusInput, setNewStatusInput,
    newStatusColor, setNewStatusColor,
    handleAddStatusOption,
    handleOpenEditStatusModal, handleUpdateEditingStatus,
    handleMoveEditingStatus,
    handleDeleteEditingStatus, handleAddNewStatusInModal,
    handleSaveAllStatusEdits,
    isEditAlbumStatusModalOpen, setIsEditAlbumStatusModalOpen,
    editingAlbumStatuses, newAlbumStatusInput, setNewAlbumStatusInput,
    newAlbumStatusColor, setNewAlbumStatusColor,
    handleOpenEditAlbumStatusModal, handleUpdateEditingAlbumStatus,
    handleMoveEditingAlbumStatus,
    handleDeleteEditingAlbumStatus, handleAddNewAlbumStatusInModal,
    handleSaveAllAlbumStatusEdits,
  } = useStatusEditor(saveStateRef, statusOptions, setStatusOptions, albumStatusOptions, setAlbumStatusOptions, tasks, setTasks, albums, setAlbums, currentUser, taskStatusConfig, setTaskStatusConfig);

  const updateNotificationSettings = useCallback((updater) => {
    setNotificationSettings(prev => {
      const safePrev = prev && typeof prev === 'object' ? prev : DEFAULT_NOTIFICATION_SETTINGS;
      const next = typeof updater === 'function' ? updater(safePrev) : updater;
      saveStateToLocalStorage({ notificationSettings: next });
      return next;
    });
  }, [saveStateToLocalStorage]);

  const inferNotificationEvent = useCallback((notification) => {
    if (notification?.eventKey) return notification.eventKey;
    const typeToEvent = {
      task_assigned: 'assigned',
      album_assigned: 'assigned',
      task_status_changed: 'taskStatusChanged',
      task_due_date_changed: 'taskDueDateChanged',
      task_overdue_alert: 'overdueAlert',
      task_comment_added: 'commentAdded',
      task_update_created: 'commentAdded',
      task_update_edited: 'commentAdded',
      task_completed: 'taskCompleted',
      album_status_changed: 'albumStatusChanged',
      album_print_sent: 'albumPrintSent',
      album_print_finished: 'albumPrintFinished',
      album_eta_changed: 'albumEtaChanged',
      album_material_delivered: 'albumMaterialDelivered',
      album_review_changes: 'albumReviewChangesRequested',
      album_review_approved: 'albumReviewApproved',
      mention: 'mentioned',
      automation_notify: 'automations',
    };
    return typeToEvent[notification?.type] || null;
  }, []);

  const inferNotificationDomain = useCallback((notification) => {
    if (notification?.domain) return notification.domain;
    const eventKey = inferNotificationEvent(notification);
    if (!eventKey) return 'system';
    if (eventKey.startsWith('album')) return 'albums';
    if (eventKey.startsWith('task') || eventKey === 'mentioned' || eventKey === 'assigned' || eventKey === 'overdueAlert' || eventKey === 'commentAdded') return 'tasks';
    return 'system';
  }, [inferNotificationEvent]);

  const shouldUserReceiveNotification = useCallback((user, notification) => {
    const settings = notificationSettings || DEFAULT_NOTIFICATION_SETTINGS;
    if (settings.globalMuteAll && !notification?.bypassGlobalMute) return false;
    if (!user) return true;

    const userPrefs = normalizeUserNotificationPrefs(settings.users?.[user.id], user.role);
    if (userPrefs.mode === 'mute') return false;
    if (userPrefs.mode === 'all') return true;

    const domain = inferNotificationDomain(notification);
    if (domain === 'tasks' && !userPrefs.scope.tasks) return false;
    if (domain === 'albums' && !userPrefs.scope.albums) return false;

    const eventKey = inferNotificationEvent(notification);
    if (eventKey && userPrefs.events[eventKey] === false && !notification?.bypassEventFilter) return false;
    return true;
  }, [inferNotificationDomain, inferNotificationEvent, notificationSettings]);

  const shouldTriggerImmediateAlert = useCallback((user, notification) => {
    if (!user) return true;
    const settings = notificationSettings || DEFAULT_NOTIFICATION_SETTINGS;
    const userPrefs = normalizeUserNotificationPrefs(settings.users?.[user.id], user.role);
    if (userPrefs.mode === 'mute') return false;
    if (userPrefs.mode === 'custom' && userPrefs.frequency !== 'realtime' && notification?.priority !== 'critical') {
      return false;
    }
    return !isNowInQuietHours(getBrazilDate(), userPrefs.quietHours);
  }, [getBrazilDate, notificationSettings]);

  const shouldAllowChannel = useCallback((user, channel) => {
    if (!user) return true;
    const settings = notificationSettings || DEFAULT_NOTIFICATION_SETTINGS;
    const userPrefs = normalizeUserNotificationPrefs(settings.users?.[user.id], user.role);
    if (userPrefs.mode === 'all') return true;
    if (userPrefs.mode === 'mute') return false;
    return userPrefs.channels?.[channel] ?? true;
  }, [notificationSettings]);

  const triggerBrowserNotification = useCallback((notif) => {
    if (typeof Notification === 'undefined') return;
    const eventKey = inferNotificationEvent(notif);
    const isTaskAssignment = eventKey === 'assigned' && notif?.type === 'task_assigned';

    const options = {
      body: notif.message,
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      tag: notif.browserTag || (isTaskAssignment ? `task-assigned-${notif.taskId || notif.id}` : `notif-${notif.id}`),
      renotify: notif.renotify ?? isTaskAssignment,
      requireInteraction: notif.requireInteraction ?? isTaskAssignment,
      silent: false,
      data: {
        route: 'gestao_photofine',
        taskId: notif.taskId || null,
        notificationType: notif.type || 'generic',
      },
    };

    const showBrowserNotification = () => {
      try {
        // eslint-disable-next-line no-new
        new Notification(notif.title, options);
      } catch (error) {
        console.error('Erro ao disparar notificação de navegador:', error);
      }
    };

    if (Notification.permission === 'granted') {
      showBrowserNotification();
      return;
    }

    if (Notification.permission === 'default') {
      Notification.requestPermission().then((permission) => {
        if (permission === 'granted') showBrowserNotification();
      });
    }
  }, [inferNotificationEvent]);

  const addNotificationWithPolicies = useCallback((notification) => {
    const settings = notificationSettings || DEFAULT_NOTIFICATION_SETTINGS;
    if (settings.globalMuteAll && !notification?.bypassGlobalMute) return;

    const nowIso = getBrazilDate().toISOString();
    const base = {
      id: Date.now() + Math.random(),
      timestamp: nowIso,
      read: false,
      ...notification,
    };
    const dedupWindowMs = Math.max(0, (settings.antiNoiseWindowMinutes || 0)) * 60 * 1000;

    const recipientMembers = (() => {
      if (Array.isArray(base.recipientIds) && base.recipientIds.length > 0) {
        return members.filter(m => base.recipientIds.includes(m.id));
      }
      if (base.recipientId) {
        const matched = members.find(m => m.id === base.recipientId);
        return matched ? [matched] : [];
      }
      if (base.recipientRole === 'admin') {
        return members.filter(m => m.role === 'admin');
      }
      return [];
    })();

    const targetNotifications = recipientMembers.length > 0
      ? recipientMembers.map(member => ({ ...base, recipientId: member.id, recipientEmail: member.email }))
      : [{ ...base }];

    const acceptedNotifications = targetNotifications.filter((notif) => {
      const recipient = notif.recipientId ? members.find(m => m.id === notif.recipientId) : null;
      if (!shouldUserReceiveNotification(recipient, notif)) return false;

      const eventKey = inferNotificationEvent(notif) || notif.type || 'generic';
      const entityKey = notif.taskId || notif.albumId || notif.entityId || notif.taskName || notif.albumName || notif.title;
      const dedupKey = `${notif.recipientId || 'broadcast'}|${eventKey}|${entityKey}`;
      const lastSeen = notificationDedupRef.current[dedupKey] || 0;
      if (dedupWindowMs > 0 && notif.priority !== 'critical' && Date.now() - lastSeen < dedupWindowMs) {
        return false;
      }
      notificationDedupRef.current[dedupKey] = Date.now();
      return true;
    });

    if (acceptedNotifications.length === 0) return;

    acceptedNotifications.forEach((notif) => {
      const isRecipient = !notif.recipientId || notif.recipientId === currentUser?.id;
      if (!isRecipient || !currentUser) return;

      if (shouldAllowChannel(currentUser, 'inApp')) {
        setNotifications(prev => [notif, ...prev]);
        setUnreadCount(prev => prev + 1);
      }
      if (shouldAllowChannel(currentUser, 'toast') && shouldTriggerImmediateAlert(currentUser, notif)) {
        addToast(notif.title, notif.message, notif.type);
      }
      const immediateAllowed = shouldTriggerImmediateAlert(currentUser, notif) || !!notif.forceBrowser;
      const browserAllowed = shouldAllowChannel(currentUser, 'browser') || !!notif.forceBrowser;
      if (browserAllowed && immediateAllowed) {
        triggerBrowserNotification(notif);
      }
    });

    const existingData = localStorage.getItem('photofine_data');
    const currentData = existingData ? JSON.parse(existingData) : {};
    const allNotifications = currentData.notifications || [];
    allNotifications.push(...acceptedNotifications);
    saveStateToLocalStorage({ notifications: allNotifications });
  }, [
    currentUser,
    getBrazilDate,
    inferNotificationEvent,
    members,
    notificationSettings,
    saveStateToLocalStorage,
    shouldAllowChannel,
    shouldTriggerImmediateAlert,
    shouldUserReceiveNotification,
    triggerBrowserNotification,
    addToast,
  ]);

  // Funções de notificação — definidas ANTES de useTaskActions (que as utiliza)
  const addNotification = (notification) => {
    if (addNotificationWithPolicies) {
      addNotificationWithPolicies(notification);
      return;
    }
    const newNotification = {
      id: Date.now() + Math.random(),
      timestamp: getBrazilDate().toISOString(),
      read: false,
      ...notification
    };
    // Verificar se a notificação é para o usuário atual
    const isRecipient = !newNotification.recipientId || newNotification.recipientId === currentUser?.id;
    
    // Adicionar ao estado apenas se for para este usuário
    if (isRecipient && currentUser) {
      setNotifications(prev => [newNotification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Mostrar toast
      addToast(newNotification.title, newNotification.message, newNotification.type);
      
      // Notificação do navegador (se permitido)
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        new Notification(newNotification.title, {
          body: newNotification.message,
          icon: '/favicon.ico'
        });
      }
    }
    
    // Persistir notificação no localStorage (para todos, o destinatário vê quando logar)
    const existingData = localStorage.getItem('photofine_data');
    const currentData = existingData ? JSON.parse(existingData) : {};
    const allNotifications = currentData.notifications || [];
    allNotifications.push(newNotification);
    saveStateToLocalStorage({ notifications: allNotifications });
  };

  const {
    handleAddTask, handleCreateTaskFromCalendar, updateTask,
    handleAddUpdate, handleLikeUpdate, handleReplyUpdate,
    handleDeleteUpdate, handleEditUpdate,
  } = useTaskActions(saveStateRef, tasks, setTasks, groups, members, currentUser, getBrazilDate, getBrazilDateStr, addNotification, addToast, setReplyingTo, editingUpdateId, setEditingUpdateId, setEditingUpdateText, setActivePanelTaskId, theme);

  const {
    handleAddGroup, handleAddCategory,
    handleDeleteGroup, handleDeleteCategory,
    updateGroupTitle, updateCategoryTitle, updateGroupColor,
  } = useGroupActions(saveStateRef, groups, setGroups, categories, setCategories, tasks, setTasks, currentUser, setConfirmDialog, setActiveColorPickerGroupId, theme);

  // Carregar dados do localStorage ao iniciar (instantâneo)
  useEffect(() => {
    const loadData = () => {
      try {
        const savedData = localStorage.getItem('photofine_data');
        
        if (savedData) {
          const data = JSON.parse(savedData);
          console.log('✅ Dados carregados do localStorage');
          
          if (data.tasks) {
            const tasksWithDataCriacao = data.tasks.map(task => {
              if (!task.dataCriacao) {
                return { ...task, dataCriacao: getBrazilDateStr() };
              }
              return task;
            });
            setTasks(tasksWithDataCriacao);
          }
          if (data.groups) setGroups(data.groups);
          if (data.categories) setCategories(data.categories);
          if (data.statusOptions) setStatusOptions(data.statusOptions);
          if (data.taskStatusConfig) setTaskStatusConfig(data.taskStatusConfig);
          if (data.columns) {
            let updatedColumns = [...data.columns];
            let hasChanges = false;
            
            const hasDataCriacao = updatedColumns.some(col => col.id === 'dataCriacao');
            if (!hasDataCriacao) {
              updatedColumns = [{ id: 'dataCriacao', title: 'Data Tarefa', type: 'readonly', width: 100 }, ...updatedColumns];
              hasChanges = true;
            }
            
            const hasPrazo = updatedColumns.some(col => col.id === 'prazo');
            if (!hasPrazo) {
              const entregaIndex = updatedColumns.findIndex(col => col.id === 'entrega');
              if (entregaIndex !== -1) {
                updatedColumns.splice(entregaIndex, 0, { id: 'prazo', title: 'Prazo', type: 'prazo', width: 80 });
              } else {
                updatedColumns.push({ id: 'prazo', title: 'Prazo', type: 'prazo', width: 80 });
              }
              hasChanges = true;
            }
            
            if (hasChanges) {
              setColumns(updatedColumns);
              saveState({ columns: updatedColumns });
            } else {
              setColumns(data.columns);
            }
          }
          if (data.members) setMembers(data.members);
          if (data.theme) setTheme(prev => ({ ...INITIAL_THEME, ...data.theme }));
          if (data.albums) setAlbums(data.albums);
          if (data.albumStatusOptions) setAlbumStatusOptions(data.albumStatusOptions);
          if (data.hiddenColumns) setHiddenColumns(data.hiddenColumns);
          if (data.notificationSettings) setNotificationSettings(data.notificationSettings);
          else setNotificationSettings(DEFAULT_NOTIFICATION_SETTINGS);
          
          if (data.notifications) {
            // Carregar todas as notificações; filtrar por usuário quando currentUser estiver disponível
            setNotifications(data.notifications);
            setUnreadCount(data.notifications.filter(n => !n.read).length);
          }
        } else {
          console.log('✅ Nenhum dado encontrado, usando dados iniciais');
          saveState({
            tasks: INITIAL_TASKS,
            groups: INITIAL_GROUPS,
            categories: INITIAL_CATEGORIES,
            columns: INITIAL_COLUMNS,
            statusOptions: INITIAL_STATUS_COLORS,
            albumStatusOptions: ALBUM_STATUS_COLORS,
            taskStatusConfig: INITIAL_TASK_STATUS_CONFIG,
            members: [],
            theme: INITIAL_THEME,
            albums: INITIAL_ALBUMS,
            notificationSettings: DEFAULT_NOTIFICATION_SETTINGS
          });
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };
    
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync e Socket.io — fornecidos por useSync

  const updateTheme = (field, value) => {
    const newTheme = { ...theme, [field]: value };
    setTheme(newTheme);
    saveStateToLocalStorage({ theme: newTheme });
  };

  // Função para exportar backup completo para arquivo JSON
  const exportBackupToFile = () => {
    const data = {
      tasks,
      groups,
      categories,
      columns,
      statusOptions,
      members,
      theme,
      albums,
      notificationSettings,
      currentUser,
      exportDate: getBrazilDate().toISOString(),
      version: '1.0'
    };
    
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `photofine_backup_${getBrazilDateStr()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    console.log(' Backup exportado para arquivo');
  };

  // Função para importar backup de arquivo JSON
  const importBackupFromFile = (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const raw = JSON.parse(e.target.result);
        const data = raw.data || raw; // Suporta formato do servidor ({data:...}) e formato direto
        
        // Confirmar importação
        if (window.confirm(`Deseja restaurar o backup de ${raw.exportDate || 'data desconhecida'}?\n\nIsso substituirá todos os dados atuais.`)) {
          restoreFullState(data);
          
          // Restaurar no servidor via API
          try {
            await api.restoreBackup(data);
            console.log('✅ Backup restaurado no servidor');
          } catch (err) {
            console.warn('Erro ao restaurar no servidor:', err.message);
          }
          
          alert('Backup restaurado com sucesso!');
          console.log('✅ Backup importado de arquivo');
        }
      } catch (error) {
        console.error('Erro ao importar backup:', error);
        alert('Erro ao importar backup. Verifique se o arquivo é válido.');
      }
    };
    reader.readAsText(file);
    event.target.value = ''; // Reset input
  };

  // Backup automático a cada 5 minutos (mantém últimos 5 backups no localStorage)
  useEffect(() => {
    const autoBackupInterval = setInterval(() => {
      // Ler dados atuais do localStorage (sempre atualizado pelo saveState)
      const savedData = localStorage.getItem('photofine_data');
      if (savedData) {
        const data = JSON.parse(savedData);
        const backup = {
          tasks: data.tasks,
          groups: data.groups,
          categories: data.categories,
          columns: data.columns,
          statusOptions: data.statusOptions,
          members: data.members,
          theme: data.theme,
          albums: data.albums,
          notificationSettings: data.notificationSettings || DEFAULT_NOTIFICATION_SETTINGS,
          backupDate: getBrazilDate().toISOString()
        };
        
        const existingBackups = JSON.parse(localStorage.getItem('photofine_auto_backups') || '[]');
        existingBackups.push(backup);
        if (existingBackups.length > 5) existingBackups.shift();
        localStorage.setItem('photofine_auto_backups', JSON.stringify(existingBackups));
        console.log(' Auto-backup criado');
      }
    }, 5 * 60 * 1000); // 5 minutos

    return () => clearInterval(autoBackupInterval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const restoreFullState = (data) => {
    if (data.tasks) setTasks(data.tasks);
    if (data.groups) setGroups(data.groups);
    if (data.categories) setCategories(data.categories);
    if (data.columns) setColumns(data.columns);
    if (data.statusOptions) setStatusOptions(data.statusOptions);
    if (data.albumStatusOptions) setAlbumStatusOptions(data.albumStatusOptions);
    if (data.members) setMembers(data.members);
    if (data.theme) setTheme({ ...INITIAL_THEME, ...data.theme });
    if (data.albums) setAlbums(data.albums);
    if (data.notificationSettings) setNotificationSettings(data.notificationSettings);
    if (data.notifications) { setNotifications(data.notifications); setUnreadCount(data.notifications.filter(n => !n.read).length); }
    saveStateToLocalStorage(data);
  };

  // addToast vem do hook useToasts

  const markNotificationAsRead = (id) => {
    setNotifications(prev => {
      const updated = prev.map(n => n.id === id ? { ...n, read: true } : n);
      // Persistir no localStorage e servidor
      const existingData = localStorage.getItem('photofine_data');
      if (existingData) {
        const data = JSON.parse(existingData);
        if (data.notifications) {
          data.notifications = data.notifications.map(n => n.id === id ? { ...n, read: true } : n);
          saveStateToLocalStorage({ notifications: data.notifications });
        }
      }
      return updated;
    });
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllNotificationsAsRead = () => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      // Persistir no localStorage e servidor
      const existingData = localStorage.getItem('photofine_data');
      if (existingData) {
        const data = JSON.parse(existingData);
        if (data.notifications) {
          data.notifications = data.notifications.map(n => {
            // Atualizar apenas notificações do usuário atual
            if (!n.recipientId || n.recipientId === currentUser?.id) {
              return { ...n, read: true };
            }
            return n;
          });
          saveStateToLocalStorage({ notifications: data.notifications });
        }
      }
      return updated;
    });
    setUnreadCount(0);
  };

  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    // Persistir limpeza no localStorage e servidor
    const existingData = localStorage.getItem('photofine_data');
    if (existingData) {
      const data = JSON.parse(existingData);
      if (data.notifications) {
        // Remover apenas notificações do usuário atual
        data.notifications = data.notifications.filter(n =>
          n.recipientId && n.recipientId !== currentUser?.id
        );
        saveStateToLocalStorage({ notifications: data.notifications });
      }
    }
  };

  // Notificações clicáveis: dispatch baseado no tipo, abrindo o destino relevante.
  // - album_review_changes / album_review_approved → modal de revisão do álbum (aba Anotações / Aprovação)
  // - task_* / mention → painel lateral da tarefa + aba Kanban
  // - album_* (qualquer outro) → modal de revisão do álbum (aba Acesso)
  const openAlbumReview = useCallback((albumId, initialTab = 'access') => {
    if (!albumId) return;
    setPendingReviewOpen({ albumId, initialTab, nonce: Date.now() + Math.random() });
    setCurrentRoute('albuns');
    setIsNotificationsOpen(false);
  }, []);

  const clearPendingReviewOpen = useCallback((nonce) => {
    setPendingReviewOpen((current) => (current && current.nonce === nonce ? null : current));
  }, []);

  const handleNotificationNavigate = useCallback((notification) => {
    if (!notification) return;
    const type = notification.type || '';
    if (type === 'album_review_changes' || type === 'album_review_approved') {
      const tab = type === 'album_review_changes' ? 'notes' : 'approval';
      openAlbumReview(notification.albumId, tab);
    } else if (type.startsWith('task_') || type === 'mention') {
      setCurrentRoute('gestao_photofine');
      setActiveTab('kanban');
      if (notification.taskId != null) setActivePanelTaskId(notification.taskId);
      setIsNotificationsOpen(false);
    } else if (notification.albumId != null) {
      openAlbumReview(notification.albumId, 'access');
    } else {
      setIsNotificationsOpen(false);
    }
  }, [openAlbumReview]);

  const clearAllTaskActivityLogs = () => {
    const cleanedTasks = tasks.map(task => ({ ...task, activityLog: [] }));
    setTasks(cleanedTasks);
    saveStateToLocalStorage({ tasks: cleanedTasks });
  };

  // Função para carregar notificações do usuário logado
  const loadUserNotifications = useCallback((user) => {
    const existingData = localStorage.getItem('photofine_data');
    if (existingData) {
      const data = JSON.parse(existingData);
      if (data.notifications) {
        // Filtrar notificações para o usuário logado
        const userNotifications = data.notifications.filter(n => 
          !n.recipientId || // Notificações sem destinatário (geral)
          n.recipientId === user?.id // Notificações específicas para este usuário
        );
        const filteredByPreferences = userNotifications.filter(n => shouldUserReceiveNotification(user, n));
        setNotifications(filteredByPreferences);
        setUnreadCount(filteredByPreferences.filter(n => !n.read).length);
      }
    }
  }, [shouldUserReceiveNotification]);

  // Filtrar notificações do usuário quando currentUser muda
  useEffect(() => {
    if (currentUser) {
      const currentMember = members.find(m => m.id === currentUser.id) || currentUser;
      loadUserNotifications(currentMember);
    } else {
      // Limpar notificações ao fazer logout
      setNotifications([]);
      setUnreadCount(0);
    }
  }, [currentUser, members, loadUserNotifications]);

  useEffect(() => {
    if (!currentUser?.id) return;
    if (notificationSettings?.users?.[currentUser.id]) return;
    updateNotificationSettings(prev => {
      const users = { ...(prev?.users || {}) };
      users[currentUser.id] = createDefaultUserNotificationPrefs(currentUser.role);
      return { ...(prev || DEFAULT_NOTIFICATION_SETTINGS), users };
    });
  }, [currentUser, notificationSettings, updateNotificationSettings]);

  // Solicitar permissão de notificação ao carregar
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // handleColDragStart, handleColDrop, handleAddNewColumn, handleDeleteColumn,
  // toggleColumnVisibility, showAllColumns, unhideColumn — fornecidos por useColumns
  // handleAddStatusOption, handleOpenEditStatusModal, handleUpdateEditingStatus, etc. — fornecidos por useStatusEditor
  // renderCellContent — extraído para componente TaskCellContent

  const isArchivedTab = activeTab === 'arquivadas';

  const filteredTasks = useMemo(() => tasks.filter(t => {
    const matchSearch = t.elemento.toLowerCase().includes(searchTerm.toLowerCase());
    const matchPerson = personFilter ? (t.equipe || []).includes(personFilter) : true;
    const matchStatus = statusFilter ? t.status === statusFilter : true;
    const matchRole = currentUser?.role === 'admin' ? true : (t.equipe || []).includes(currentUser?.initials);
    const matchArchive = isArchivedTab ? !!t.archived : !t.archived;
    return matchSearch && matchPerson && matchStatus && matchRole && matchArchive;
  }).sort((a, b) => {
    if (!sortConfig.field) return 0;
    let valA = a[sortConfig.field] || ''; let valB = b[sortConfig.field] || '';
    if (sortConfig.field === 'priority') {
      const order = { 'ALTA': 1, 'MÉDIA': 2, 'BAIXA': 3, 'ENCERRADO': 4, 'VAZIO': 5 };
      valA = order[a.priority] || 99; valB = order[b.priority] || 99;
    } else if (sortConfig.field === 'status') {
      const order = { 'AGUARDANDO': 1, 'EDIÇÃO': 2, 'REVISAR': 3, 'POSTADO': 4, 'VAZIO': 5 };
      valA = order[a.status] || 99; valB = order[b.status] || 99;
    } else if (sortConfig.field === 'entrega') {
      valA = a.entrega || '9999-99-99';
      valB = b.entrega || '9999-99-99';
    } else if (typeof valA === 'string') {
      valA = valA.toLowerCase(); valB = valB.toLowerCase();
    }
    if (valA < valB) return sortConfig.order === 'asc' ? -1 : 1;
    if (valA > valB) return sortConfig.order === 'asc' ? 1 : -1;
    return 0;
  }), [tasks, searchTerm, personFilter, statusFilter, sortConfig, currentUser, isArchivedTab]);

  const handleReorderTaskStatuses = useCallback((sourceStatus, targetStatus) => {
    if (currentUser?.role !== 'admin') return;
    if (!sourceStatus || !targetStatus || sourceStatus === targetStatus) return;

    const orderedStatuses = Object.entries(statusOptions).filter(([key]) => key !== 'VAZIO');
    const fromIndex = orderedStatuses.findIndex(([key]) => key === sourceStatus);
    const toIndex = orderedStatuses.findIndex(([key]) => key === targetStatus);
    if (fromIndex < 0 || toIndex < 0) return;

    const reordered = [...orderedStatuses];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const nextStatusOptions = {};
    if (statusOptions.VAZIO) {
      nextStatusOptions.VAZIO = statusOptions.VAZIO;
    }
    reordered.forEach(([key, value]) => {
      nextStatusOptions[key] = value;
    });

    setStatusOptions(nextStatusOptions);
    saveStateToLocalStorage({ statusOptions: nextStatusOptions });
  }, [currentUser, saveStateToLocalStorage, setStatusOptions, statusOptions]);

  const activeTask = tasks.find(t => t.id === activePanelTaskId);

  const normalizeTextForMatch = useCallback((value) => {
    return String(value || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }, []);

  const extractDigits = useCallback((value) => String(value || '').replace(/\D/g, ''), []);

  const normalizePhoneForWhatsApp = useCallback((value) => {
    const digits = extractDigits(value);
    if (!digits) return '';
    if (digits.length <= 11 && !digits.startsWith('55')) return `55${digits}`;
    return digits;
  }, [extractDigits]);

  const getClientWhatsAppColumn = useCallback(() => {
    return columns.find((col) => {
      const title = normalizeTextForMatch(col?.title);
      return title === 'whatsapp cliente' || title === 'whatspp cliente' || (title.includes('whats') && title.includes('cliente'));
    }) || null;
  }, [columns, normalizeTextForMatch]);

  const getClientWhatsAppFromTask = useCallback((task) => {
    if (!task) return '';
    const clientCol = getClientWhatsAppColumn();
    if (!clientCol) return '';
    return normalizePhoneForWhatsApp(task[clientCol.id]);
  }, [getClientWhatsAppColumn, normalizePhoneForWhatsApp]);

  const getTaskTeamMembersWithWhatsApp = useCallback((task) => {
    if (!task) return [];
    const teamInitials = Array.isArray(task.equipe) ? task.equipe : [];
    return teamInitials
      .map((initials) => {
        const member = members.find((m) => m.initials === initials);
        if (!member) return null;
        let whatsappDigits = normalizePhoneForWhatsApp(member.whatsapp);
        if (!whatsappDigits && String(member.id) === String(currentUser?.id)) {
          whatsappDigits = normalizePhoneForWhatsApp(currentUser?.whatsapp);
        }
        if (!whatsappDigits) return null;
        return { ...member, whatsappDigits };
      })
      .filter(Boolean);
  }, [members, normalizePhoneForWhatsApp, currentUser]);

  const openWhatsAppShareModal = useCallback((task, update) => {
    if (!task || !update?.text?.trim()) {
      addToast('Envio para WhatsApp', 'Essa nota não possui texto para envio.', 'warning');
      return;
    }
    const availableMembers = getTaskTeamMembersWithWhatsApp(task);
    setWhatsAppShareModal({
      taskId: task.id,
      updateId: update.id,
      text: update.text,
    });
    setWhatsAppShareTarget('client');
    setWhatsAppShareMember(availableMembers[0]?.initials || '');
  }, [addToast, getTaskTeamMembersWithWhatsApp]);

  const closeWhatsAppShareModal = useCallback(() => {
    setWhatsAppShareModal(null);
    setWhatsAppShareTarget('client');
    setWhatsAppShareMember('');
    setIsSendingWhatsAppNote(false);
  }, []);

  const handleSendUpdateToWhatsApp = useCallback(async () => {
    if (isSendingWhatsAppNote) return;
    if (!whatsAppShareModal?.taskId || !whatsAppShareModal?.text) return;

    const task = tasks.find((t) => t.id === whatsAppShareModal.taskId);
    if (!task) {
      addToast('Envio para WhatsApp', 'Não foi possível localizar a tarefa da nota.', 'error');
      closeWhatsAppShareModal();
      return;
    }

    let targetPhone = '';
    if (whatsAppShareTarget === 'client') {
      targetPhone = getClientWhatsAppFromTask(task);
      if (!targetPhone) {
        addToast('WhatsApp do cliente', 'Informe o número na coluna "Whatsapp Cliente" antes de enviar.', 'warning');
        return;
      }
    } else {
      const availableMembers = getTaskTeamMembersWithWhatsApp(task);
      const selectedMember = availableMembers.find((member) => member.initials === whatsAppShareMember);
      if (!selectedMember?.whatsappDigits) {
        addToast('WhatsApp da equipe', 'Selecione um membro com número de WhatsApp cadastrado.', 'warning');
        return;
      }
      targetPhone = selectedMember.whatsappDigits;
    }

    setIsSendingWhatsAppNote(true);
    try {
      await api.sendWhatsAppMessage({
        to: targetPhone,
        text: whatsAppShareModal.text,
        meta: {
          source: 'task_update',
          targetType: whatsAppShareTarget,
          taskId: task.id,
          updateId: whatsAppShareModal.updateId || null
        }
      });
      addToast('Envio para WhatsApp', 'Mensagem enviada com sucesso pela Evolution API.', 'success');
      closeWhatsAppShareModal();
    } catch (error) {
      addToast('Envio para WhatsApp', error.message || 'Não foi possível enviar a mensagem pelo WhatsApp.', 'error');
    } finally {
      setIsSendingWhatsAppNote(false);
    }
  }, [
    addToast,
    closeWhatsAppShareModal,
    getClientWhatsAppFromTask,
    getTaskTeamMembersWithWhatsApp,
    isSendingWhatsAppNote,
    tasks,
    whatsAppShareMember,
    whatsAppShareModal,
    whatsAppShareTarget,
  ]);

  const whatsAppShareTask = useMemo(() => {
    if (!whatsAppShareModal?.taskId) return null;
    return tasks.find((task) => task.id === whatsAppShareModal.taskId) || null;
  }, [tasks, whatsAppShareModal]);

  const whatsAppShareTeamMembers = useMemo(() => {
    return getTaskTeamMembersWithWhatsApp(whatsAppShareTask);
  }, [getTaskTeamMembersWithWhatsApp, whatsAppShareTask]);

  const whatsAppShareClientColumn = useMemo(() => getClientWhatsAppColumn(), [getClientWhatsAppColumn]);
  const whatsAppShareClientPhone = useMemo(() => getClientWhatsAppFromTask(whatsAppShareTask), [getClientWhatsAppFromTask, whatsAppShareTask]);

  const whatsAppNotifyTasks = useMemo(() => {
    if (!whatsAppNotifyModal?.taskIds) return [];
    return tasks.filter((task) => whatsAppNotifyModal.taskIds.includes(task.id));
  }, [tasks, whatsAppNotifyModal]);

  const whatsAppNotifyMembers = useMemo(() => {
    if (!whatsAppNotifyModal) return [];
    const map = new Map();
    whatsAppNotifyTasks.forEach((task) => {
      getTaskTeamMembersWithWhatsApp(task).forEach((member) => {
        if (!map.has(member.initials)) {
          map.set(member.initials, { ...member, taskNames: [task.elemento] });
        } else {
          const existing = map.get(member.initials);
          if (!existing.taskNames.includes(task.elemento)) existing.taskNames.push(task.elemento);
        }
      });
    });
    return [...map.values()];
  }, [getTaskTeamMembersWithWhatsApp, whatsAppNotifyModal, whatsAppNotifyTasks]);

  const openWhatsAppNotifyModal = useCallback(() => {
    const selected = tasks.filter((task) => selectedTasks.includes(task.id));
    if (selected.length === 0) {
      addToast('Notificação WhatsApp', 'Selecione ao menos uma tarefa.', 'warning');
      return;
    }
    const memberMap = new Map();
    selected.forEach((task) => {
      getTaskTeamMembersWithWhatsApp(task).forEach((member) => {
        if (!memberMap.has(member.initials)) memberMap.set(member.initials, member);
      });
    });
    const availableMembers = [...memberMap.values()];
    if (availableMembers.length === 0) {
      addToast('Notificação WhatsApp', 'Nenhum membro atribuído a tarefa possui WhatsApp cadastrado.', 'warning');
      return;
    }
    setWhatsAppNotifyPriority('ALTA');
    setWhatsAppNotifyObservation('');
    setWhatsAppNotifyModal({
      taskIds: selected.map((task) => task.id),
      selectedMemberInitials: availableMembers.map((member) => member.initials),
    });
  }, [addToast, getTaskTeamMembersWithWhatsApp, selectedTasks, tasks]);

  const closeWhatsAppNotifyModal = useCallback(() => {
    setWhatsAppNotifyModal(null);
    setWhatsAppNotifyObservation('');
    setIsSendingWhatsAppNotify(false);
  }, []);

  const toggleWhatsAppNotifyMember = useCallback((initials) => {
    setWhatsAppNotifyModal((prev) => {
      if (!prev) return prev;
      const selected = prev.selectedMemberInitials || [];
      return {
        ...prev,
        selectedMemberInitials: selected.includes(initials)
          ? selected.filter((value) => value !== initials)
          : [...selected, initials],
      };
    });
  }, []);

  const buildWhatsAppNotifyMessage = useCallback((member) => {
    const list = whatsAppNotifyTasks;
    const isSingle = list.length === 1;
    const taskLabel = isSingle ? `"${list[0].elemento}"` : `${list.length} tarefas do quadro`;
    const priorityLabel = whatsAppNotifyPriority === 'URGENTE' ? 'URGENTE' : 'ALTA';
    const observation = whatsAppNotifyObservation.trim();
    const lines = [
      `${whatsAppNotifyPriority === 'URGENTE' ? '🚨' : '⚠️'} *Notificação de Tarefa*`,
      `Prioridade: *${priorityLabel}*`,
      '',
      `Olá, ${member?.name || member?.initials || 'equipe'}!`,
      `Você foi atribuído(a) a ${taskLabel}.`,
    ];
    if (isSingle) {
      lines.push(`Status: ${list[0].status || 'Sem status'}`);
      if (list[0].entrega) lines.push(`Entrega: ${list[0].entrega}`);
    } else {
      list.forEach((task) => lines.push(`• ${task.elemento}`));
    }
    if (observation) {
      lines.push('', `Observação: ${observation}`);
    }
    lines.push('', `Enviado por ${currentUser?.name || 'Gestão Photofine'}.`);
    return lines.join('\n');
  }, [currentUser, whatsAppNotifyObservation, whatsAppNotifyPriority, whatsAppNotifyTasks]);

  const handleSendWhatsAppNotify = useCallback(async () => {
    if (isSendingWhatsAppNotify || !whatsAppNotifyModal) return;
    const selectedInitials = whatsAppNotifyModal.selectedMemberInitials || [];
    const membersToNotify = whatsAppNotifyMembers.filter((member) => selectedInitials.includes(member.initials));
    if (membersToNotify.length === 0) {
      addToast('Notificação WhatsApp', 'Selecione ao menos um membro para notificar.', 'warning');
      return;
    }

    setIsSendingWhatsAppNotify(true);
    let successCount = 0;
    const failures = [];
    for (const member of membersToNotify) {
      try {
        await api.sendWhatsAppMessage({
          to: member.whatsappDigits,
          text: buildWhatsAppNotifyMessage(member),
          meta: {
            source: 'task_notification',
            taskIds: whatsAppNotifyModal.taskIds,
            priority: whatsAppNotifyPriority,
            recipientId: member.id,
          },
        });
        successCount += 1;
      } catch (error) {
        failures.push(`${member.name || member.initials}: ${error.message || 'erro desconhecido'}`);
      }
    }
    setIsSendingWhatsAppNotify(false);

    if (successCount > 0) {
      addToast('Notificação WhatsApp', `${successCount} notificação(ões) enviada(s) com sucesso.`, 'success');
    }
    if (failures.length > 0) {
      addToast('Notificação WhatsApp', `Falha ao enviar: ${failures.join(' | ')}`, 'error');
    }
    if (successCount > 0) {
      closeWhatsAppNotifyModal();
    }
  }, [
    addToast,
    buildWhatsAppNotifyMessage,
    closeWhatsAppNotifyModal,
    isSendingWhatsAppNotify,
    whatsAppNotifyMembers,
    whatsAppNotifyModal,
    whatsAppNotifyPriority,
  ]);

  // handleAddTask, handleCreateTaskFromCalendar, updateTask, handleAddUpdate,
  // handleLikeUpdate, handleReplyUpdate, handleDeleteUpdate, handleEditUpdate
  // — fornecidos por useTaskActions
  // handleAddGroup, handleAddCategory, handleDeleteGroup, handleDeleteCategory,
  // updateGroupTitle, updateCategoryTitle, updateGroupColor — fornecidos por useGroupActions

  const toggleGroupCollapse = (groupId) => setCollapsedGroups(prev => prev.includes(groupId) ? prev.filter(id => id !== groupId) : [...prev, groupId]);
  const toggleCategoryCollapse = (categoryId) => setCollapsedCategories(prev => prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]);
  const startColumnRename = useCallback((groupId, col) => {
    setEditingColId(col.id);
    setEditingColMenuKey(`${groupId}::${col.id}`);
    setEditingColTitle(col.title || '');
    setColMenuOpen(null);
  }, [setColMenuOpen]);
  const confirmColumnRename = useCallback(() => {
    if (!editingColId) return;
    handleRenameColumn(editingColId, editingColTitle);
    setEditingColId(null);
    setEditingColMenuKey(null);
    setEditingColTitle('');
  }, [editingColId, editingColTitle, handleRenameColumn]);
  const cancelColumnRename = useCallback(() => {
    setEditingColId(null);
    setEditingColMenuKey(null);
    setEditingColTitle('');
  }, []);

  // updateTask, handleAddUpdate, handleLikeUpdate, handleReplyUpdate,
  // handleDeleteUpdate, handleEditUpdate — fornecidos por useTaskActions

  const toggleTaskSelection = (taskId) => setSelectedTasks(prev => prev.includes(taskId) ? prev.filter(id => id !== taskId) : [...prev, taskId]);
  const toggleAllSelection = useCallback(() => { if (selectedTasks.length === filteredTasks.length && filteredTasks.length > 0) setSelectedTasks([]); else setSelectedTasks(filteredTasks.map(t => t.id)); }, [selectedTasks, filteredTasks]); // eslint-disable-line no-unused-vars

  useEffect(() => {
    setSelectedTasks([]);
  }, [activeTab, currentRoute]);
  
  const handleDeleteSelected = () => {
    // Admin pode excluir qualquer tarefa; editor só pode excluir tarefas que ele cadastrou
    const tasksToDelete = selectedTasks.filter(id => {
      const task = tasks.find(t => t.id === id);
      if (!task) return false;
      if (currentUser?.role === 'admin') return true;
      return task.createdBy === currentUser?.id;
    });
    const newTasks = tasks.filter(t => !tasksToDelete.includes(t.id));
    setTasks(newTasks); saveStateToLocalStorage({ tasks: newTasks }); setSelectedTasks([]);
  };

  const handleDuplicateSelected = () => {
    const tasksToDuplicate = tasks.filter(t => selectedTasks.includes(t.id));
    const maxId = tasks.length > 0 ? Math.max(...tasks.map(t => t.id)) : 0;
    const newTasks = tasksToDuplicate.map((t, index) => ({ ...t, id: maxId + index + 1, elemento: t.elemento + ' (Cópia)', groupId: t.groupId, archived: false, archivedAt: null, archivedBy: null }));
    const newTasksList = [...tasks, ...newTasks];
    setTasks(newTasksList); saveStateToLocalStorage({ tasks: newTasksList }); setSelectedTasks([]);
  };

  const handleArchiveSelected = () => {
    if (selectedTasks.length === 0) return;
    const archiveDate = getBrazilDate().toISOString();
    const updatedTasks = tasks.map(task => {
      if (!selectedTasks.includes(task.id)) return task;
      return {
        ...task,
        archived: true,
        archivedAt: task.archivedAt || archiveDate,
        archivedBy: currentUser?.id || null,
      };
    });
    setTasks(updatedTasks);
    saveStateToLocalStorage({ tasks: updatedTasks });
    addToast('Tarefas arquivadas', `${selectedTasks.length} item(ns) movido(s) para Tarefas Arquivadas.`, 'success');
    setSelectedTasks([]);
  };

  const handleUnarchiveSelected = () => {
    if (selectedTasks.length === 0) return;
    const updatedTasks = tasks.map(task => {
      if (!selectedTasks.includes(task.id)) return task;
      return {
        ...task,
        archived: false,
        archivedAt: null,
        archivedBy: null,
      };
    });
    setTasks(updatedTasks);
    saveStateToLocalStorage({ tasks: updatedTasks });
    addToast('Tarefas restauradas', `${selectedTasks.length} item(ns) retornou(aram) para o fluxo ativo.`, 'success');
    setSelectedTasks([]);
  };

  // Restaurar sessão do token salvo ao iniciar
  useEffect(() => {
    const token = api.getToken();
    if (token) {
      api.getMe().then(result => {
        setCurrentUser(result.user);
      }).catch(() => {
        // Token inválido ou expirado
        api.logout();
      });
    }
  }, []);

  const handleLogin = (user) => {
    setCurrentUser(user);
  };

  const fullStateForBackup = useMemo(() => ({ tasks, groups, categories, columns, statusOptions, albumStatusOptions, taskStatusConfig, members, theme, albums, notifications, notificationSettings }), [tasks, groups, categories, columns, statusOptions, albumStatusOptions, taskStatusConfig, members, theme, albums, notifications, notificationSettings]);

  const appStateValue = useMemo(() => ({
    currentUser, setCurrentUser,
    tasks, setTasks, updateTask,
    groups, setGroups,
    categories, setCategories,
    members, setMembers,
    albums, setAlbums,
    columns, setColumns,
    statusOptions, setStatusOptions,
    taskStatusConfig, setTaskStatusConfig,
    albumStatusOptions, setAlbumStatusOptions,
    theme, setTheme, updateTheme,
    clearAllTaskActivityLogs,
    getBrazilDate, getBrazilDateStr,
    saveStateToLocalStorage,
    addNotification,
    addToast,
    notificationSettings,
    updateNotificationSettings,
    createDefaultUserNotificationPrefs,
    setActivePanelTaskId,
    setConfirmDialog,
    fullStateForBackup,
    restoreFullState,
    openSlaReport: () => setShowSlaReport(true),
    openAlbumReview,
    clearPendingReviewOpen,
    pendingReviewOpen,
    handleNotificationNavigate,
    setActiveTab,
    handleOpenEditAlbumStatusModal: handleOpenEditAlbumStatusModal,
    handleOpenEditStatusModal: handleOpenEditStatusModal,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [currentUser, tasks, updateTask, groups, categories, members, albums, columns, statusOptions, albumStatusOptions, theme, updateTheme, getBrazilDate, getBrazilDateStr, saveStateToLocalStorage, addNotification, addToast, notificationSettings, updateNotificationSettings, setActivePanelTaskId, fullStateForBackup, restoreFullState, openAlbumReview, clearPendingReviewOpen, pendingReviewOpen, handleNotificationNavigate, handleOpenEditAlbumStatusModal, handleOpenEditStatusModal]);

  const legalPath = typeof window !== 'undefined' ? window.location.pathname : '';
  if (legalPath === '/legal/privacy') return <LegalDocumentScreen type="privacy" />;
  if (legalPath === '/legal/terms') return <LegalDocumentScreen type="terms" />;
  if (legalPath.startsWith('/escala/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-white text-slate-400"><RefreshCw size={24} className="animate-spin" /></div>}>
          <EscalaFreelancePortal />
        </Suspense>
      </ErrorBoundary>
    );
  }
  if (legalPath.startsWith('/album-review/')) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#181b26] text-slate-400"><RefreshCw size={24} className="animate-spin" /></div>}>
          <AlbumReviewPortal />
        </Suspense>
      </ErrorBoundary>
    );
  }

  if (!currentUser) return <LoginScreen onLogin={handleLogin} theme={theme} />;

  return (
    <ErrorBoundary>
    <AppStateContext.Provider value={appStateValue}>
    <Suspense fallback={<div className="flex h-screen items-center justify-center bg-[#181b26] text-slate-400"><RefreshCw size={24} className="animate-spin" /></div>}>
    <div className="flex h-screen font-sans text-sm relative overflow-hidden transition-colors duration-300" style={{ backgroundColor: theme.primaryBg, color: theme.textColor }}>
      
      {confirmDialog ? (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
          <div className="bg-[#1c1f2b] border border-slate-700 rounded-xl shadow-2xl w-full max-w-sm p-6 text-center animate-in zoom-in-95 duration-200">
             <h3 className="text-xl font-bold text-white mb-2">{confirmDialog.title}</h3>
             <p className="text-slate-400 mb-6">{confirmDialog.message}</p>
             <div className="flex gap-3 justify-center">
               <button onClick={() => setConfirmDialog(null)} className="px-4 py-2 text-slate-300 hover:bg-slate-800 rounded transition-colors">Cancelar</button>
               <button onClick={confirmDialog.onConfirm} className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded font-medium transition-colors shadow-md">Excluir Permanentemente</button>
             </div>
          </div>
        </div>
      ) : null}

      <aside className="w-64 border-r border-slate-700 flex flex-col z-10 shrink-0 transition-colors duration-300" style={{ backgroundColor: theme.sidebarBg }}>
        <div className="p-4 flex items-center gap-2 border-b border-slate-700 min-h-[72px]">
          {theme.logoUrl ? (
            <img src={theme.logoUrl} alt="Logo da Empresa" className="h-10 max-w-full object-contain" />
          ) : (
            <>
              <div className="w-6 h-6 bg-indigo-500 rounded-md flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-xs">P</span>
              </div>
              <span className="font-semibold text-white tracking-wide truncate">Photofine Pro</span>
            </>
          )}
        </div>
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          <div onClick={() => setCurrentRoute('home')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'home' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Home size={18} /> Página inicial</div>
          <div onClick={() => setCurrentRoute('my_work')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'my_work' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><CheckSquare size={18} /> O meu trabalho</div>
          <div className="pt-4 pb-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider">Áreas de trabalho</div>
          <div onClick={() => setCurrentRoute('gestao_photofine')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'gestao_photofine' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><LayoutGrid size={18} /> Gestão de Tarefas</div>
          <div onClick={() => setCurrentRoute('albuns')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'albuns' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><LayoutGrid size={18} /> Álbuns e Laboratório</div>
          <div onClick={() => setCurrentRoute('social')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'social' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Share2 size={18} /> Redes Sociais</div>
          {currentUser?.role === 'admin' ? (
            <>
              <div className="pt-4 pb-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-t border-slate-700/50 mt-2">Administração</div>
              <div onClick={() => setCurrentRoute('team_management')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'team_management' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Users size={18} /> Gestão de Equipe</div>
              <div onClick={() => setCurrentRoute('clients')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'clients' ? 'bg-cyan-900/40 text-cyan-400 border border-cyan-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><UserPlus size={18} /> Clientes</div>
              <div onClick={() => setCurrentRoute('locacoes')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'locacoes' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><CalendarRange size={18} /> Locações de Equipamentos</div>
              <div onClick={() => setCurrentRoute('notification_center')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'notification_center' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Bell size={18} /> Central de Notificações</div>
              <div onClick={() => setCurrentRoute('message_hub')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'message_hub' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><MessageSquare size={18} /> Gestão de Mensagens</div>
              <div onClick={() => setCurrentRoute('sales_agent')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'sales_agent' ? 'bg-purple-900/40 text-purple-400 border border-purple-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Bot size={18} /> Agente de Vendas</div>
              <div onClick={() => setCurrentRoute('settings')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'settings' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Settings size={18} /> Configurações</div>
              <a href="https://photofine.com.br/guto" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors hover:bg-slate-800 text-slate-300 no-underline"><ExternalLink size={18} /> Adm Photofine</a>
            </>
          ) : (
            <>
              <div className="pt-4 pb-2 px-3 text-xs font-semibold text-slate-500 uppercase tracking-wider border-t border-slate-700/50 mt-2">Minha Conta</div>
              <div onClick={() => setCurrentRoute('team_management')} className={`flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors ${currentRoute === 'team_management' ? 'bg-blue-900/40 text-blue-400 border border-blue-800/50' : 'hover:bg-slate-800 text-slate-300'}`}><Users size={18} /> Meu Perfil</div>
            </>
          )}
        </nav>
        {(() => {
          const fe = buildInfo || {};
          const be = serverBuildInfo || {};
          const feShort = fe.shortSha || (fe.sha ? fe.sha.slice(0, 7) : '?');
          const beShort = be.shortSha || (be.sha ? be.sha.slice(0, 7) : null);
          const sameCommit = beShort && feShort && beShort === feShort;
          const dirty = fe.isDirty === '1' || be.isDirty === '1';
          const dateStr = fe.commitDate ? new Date(fe.commitDate).toLocaleDateString('pt-BR') : '';
          return (
            <button
              type="button"
              onClick={() => setShowBuildInfoModal(true)}
              title="Informacoes de build (clique para detalhes)"
              className="mt-auto px-3 py-2 mx-2 mb-3 text-left text-[10px] font-mono leading-tight text-slate-500 hover:text-slate-300 hover:bg-slate-800/40 rounded transition-colors cursor-pointer border border-transparent hover:border-slate-700/50"
            >
              <div className="flex items-center gap-1.5">
                <span className={`inline-block w-1.5 h-1.5 rounded-full ${sameCommit ? 'bg-emerald-500' : 'bg-amber-500'}`} title={sameCommit ? 'Frontend e backend no mesmo commit' : 'Frontend e backend em commits diferentes'}></span>
                <span className="font-semibold">build {feShort}{dirty ? '*' : ''}</span>
                {dateStr ? <span className="opacity-60">· {dateStr}</span> : null}
              </div>
              {beShort ? (
                <div className="opacity-50 mt-0.5">api: {beShort}{be.isDirty === '1' ? '*' : ''}</div>
              ) : (
                <div className="opacity-40 mt-0.5">api: ?</div>
              )}
            </button>
          );
        })()}
      </aside>

      <main className="flex-1 flex flex-col overflow-hidden relative z-0">
        <header className="h-14 shrink-0 border-b border-slate-700 flex items-center justify-between px-6 bg-[#181b26]">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-white">
              {currentRoute === 'gestao_photofine' ? 'Gestão de Tarefas' : currentRoute === 'albuns' ? 'Álbuns e Laboratório' : currentRoute === 'my_work' ? 'O meu trabalho' : currentRoute === 'team_management' ? (currentUser?.role === 'admin' ? 'Gestão de Equipe' : 'Meu Perfil') : currentRoute === 'clients' ? 'Clientes' : currentRoute === 'locacoes' ? 'Locações de Equipamentos' : currentRoute === 'social' ? 'Redes Sociais' : currentRoute === 'notification_center' ? 'Notificações' : currentRoute === 'message_hub' ? 'Gestão de Mensagens' : currentRoute === 'sales_agent' ? 'Agente de Vendas' : currentRoute === 'settings' ? 'Configurações' : 'Página inicial'}
            </h1>
            <ChevronDown size={16} className="text-slate-400 cursor-pointer" />
          </div>
          <div className="flex items-center gap-4 text-slate-400">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded bg-slate-800/50 border border-slate-700 mr-2 shadow-inner" title={isSyncing ? "Sincronizando com servidor..." : isOnline ? "Conectado à Nuvem" : "Modo Offline (Alterações salvas localmente)"}>
              {isSyncing ? <><RefreshCw size={14} className="text-blue-400 animate-spin" /><span className="text-[10px] font-bold text-blue-400 tracking-wider">SYNC</span></> : isOnline ? <><Wifi size={14} className="text-emerald-400" /><span className="text-[10px] font-bold text-emerald-400 tracking-wider">ONLINE</span></> : <><WifiOff size={14} className="text-red-400 animate-pulse" /><span className="text-[10px] font-bold text-red-400 tracking-wider">OFFLINE</span></>}
            </div>
            <div className="relative">
              <Bell size={18} className="cursor-pointer hover:text-white" onClick={() => setIsNotificationsOpen(!isNotificationsOpen)} />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <HelpCircle size={18} className="cursor-pointer hover:text-white" />
            
            {/* Botões de Backup */}
            <div className="relative">
              <button 
                onClick={exportBackupToFile} 
                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-emerald-400 transition-colors"
                title="Exportar Backup"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              </button>
            </div>
            <div className="relative">
              <label 
                className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-blue-400 transition-colors cursor-pointer"
                title="Importar Backup"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
                <input 
                  type="file" 
                  accept=".json" 
                  onChange={importBackupFromFile} 
                  className="hidden"
                />
              </label>
            </div>
            
            <div className="flex items-center gap-3 border-l border-slate-700 pl-4 ml-2">
              <div className={`w-8 h-8 rounded-full ${currentUser.color} flex items-center justify-center text-white font-bold shadow-md`}>{currentUser.initials}</div>
              <div className="hidden md:block text-xs text-left"><div className="text-white font-semibold">{currentUser.name}</div><div className="text-slate-500 capitalize">{currentUser.role}</div></div>
              <button onClick={() => { api.logout(); setCurrentUser(null); }} className="p-1.5 hover:bg-slate-700 rounded text-slate-400 hover:text-red-400 transition-colors ml-1" title="Sair do sistema"><LogOut size={16} /></button>
            </div>
          </div>
        </header>

        {currentRoute === 'home' ? (
          <HomeDashboard />
        ) : currentRoute === 'gestao_photofine' ? (
          <>
            <div className="px-6 py-4 shrink-0 border-b border-slate-700 bg-[#11131c]">
              <div className="flex gap-6 mb-4 text-sm">
                <span onClick={() => setActiveTab('quadro')} className={`pb-1 cursor-pointer transition-colors ${activeTab === 'quadro' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}>Quadro principal</span>
                <span onClick={() => setActiveTab('kanban')} className={`pb-1 cursor-pointer transition-colors ${activeTab === 'kanban' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}>Kanban</span>
                <span onClick={() => setActiveTab('calendario')} className={`pb-1 cursor-pointer transition-colors ${activeTab === 'calendario' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}>Calendário</span>
                <span onClick={() => setActiveTab('arquivadas')} className={`pb-1 cursor-pointer transition-colors ${activeTab === 'arquivadas' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}>Tarefas Arquivadas</span>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {(currentUser?.role === 'admin' || theme?.editorCanCreate) && activeTab !== 'arquivadas' && (
                  <div className="relative flex z-20">
                    <button onClick={() => handleAddTask(groups[0]?.id)} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded-l-md flex items-center font-medium transition-colors shadow-sm border-r border-blue-700/50" title="Criar novo elemento no primeiro grupo">Criar elemento</button>
                    <button onClick={() => setIsCreateMenuOpen(!isCreateMenuOpen)} className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1.5 rounded-r-md flex items-center justify-center transition-colors shadow-sm" title="Mais opções de criação"><ChevronDown size={14} /></button>
                    {isCreateMenuOpen ? (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setIsCreateMenuOpen(false)}></div>
                        <div className="absolute top-full left-0 mt-1 w-56 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl py-1 z-40 animate-in fade-in zoom-in-95 duration-100">
                          <div className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-slate-200 flex items-center gap-3 text-sm" onClick={() => { handleAddTask(groups[0]?.id); setIsCreateMenuOpen(false); }}><Box size={14} className="text-slate-400" /> Novo elemento</div>
                          <div className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-slate-200 flex items-center gap-3 text-sm" onClick={() => { handleAddGroup(categories[0]?.id); setIsCreateMenuOpen(false); }}><LayoutGrid size={14} className="text-slate-400" /> Novo grupo de elementos</div>
                          <div className="h-px bg-slate-700 my-1"></div>
                          <div className="px-4 py-2 hover:bg-slate-700 cursor-pointer text-slate-200 flex items-center gap-3 text-sm" onClick={() => { handleAddCategory(); setIsCreateMenuOpen(false); }}><FolderOpen size={14} className="text-slate-400" /> Nova Categoria (Pasta)</div>
                        </div>
                      </>
                    ) : null}
                  </div>
                  )}
                  <div className="relative ml-2">
                    <Search size={14} className="absolute left-3 top-2 text-slate-400" />
                    <input type="text" placeholder="Pesquisar..." className="bg-slate-800 border border-slate-700 rounded-full pl-8 pr-4 py-1.5 text-sm focus:outline-none focus:border-blue-500 transition-colors w-48 text-white placeholder-slate-500" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-4 text-slate-400">
                  <div className="relative">
                    <button onClick={() => setIsPersonMenuOpen(!isPersonMenuOpen)} className={`p-1.5 rounded flex items-center gap-1 transition-colors ${personFilter ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50' : 'hover:bg-slate-800'}`}><User size={14}/> Pessoa {personFilter && <span className="w-2 h-2 bg-blue-500 rounded-full ml-1"></span>}</button>
                    {isPersonMenuOpen ? (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setIsPersonMenuOpen(false)}></div>
                        <div className="absolute top-full left-0 mt-1 w-48 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl py-2 z-40 animate-in fade-in zoom-in-95 duration-100">
                          <div className="px-3 pb-2 text-xs font-semibold text-slate-400 border-b border-slate-700 mb-1">Filtrar por Pessoa</div>
                          {members.map(member => (
                            <div key={member.id} className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200" onClick={() => { setPersonFilter(personFilter === member.initials ? null : member.initials); setIsPersonMenuOpen(false); }}>
                              <div className="flex items-center gap-2"><div className={`w-5 h-5 rounded-full ${member.color} flex items-center justify-center text-[9px] font-bold text-white`}>{member.initials}</div><span>{member.name}</span></div>
                              {personFilter === member.initials ? <Check size={14} className="text-blue-400" /> : null}
                            </div>
                          ))}
                          {personFilter ? <div className="px-3 py-1.5 mt-1 hover:bg-slate-700 cursor-pointer text-slate-400 text-xs text-center border-t border-slate-700" onClick={() => {setPersonFilter(null); setIsPersonMenuOpen(false);}}>Limpar filtro</div> : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                  <div className="relative">
                    <button onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)} className={`p-1.5 rounded flex items-center gap-1 transition-colors ${statusFilter ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50' : 'hover:bg-slate-800'}`}><Filter size={14}/> Filtro {statusFilter && <span className="w-2 h-2 bg-blue-500 rounded-full ml-1"></span>}</button>
                    {isFilterMenuOpen ? (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setIsFilterMenuOpen(false)}></div>
                        <div className="absolute top-full left-0 mt-1 w-48 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl py-2 z-40 animate-in fade-in zoom-in-95 duration-100">
                          <div className="px-3 pb-2 text-xs font-semibold text-slate-400 border-b border-slate-700 mb-1">Filtrar por Status</div>
                          {Object.keys(statusOptions).filter(k => k !== 'VAZIO').map(status => {
                             const bgClass = statusOptions[status].split(' ')[0];
                             return (
                              <div key={status} className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200" onClick={() => { setStatusFilter(statusFilter === status ? null : status); setIsFilterMenuOpen(false); }}>
                                <div className="flex items-center gap-2"><div className={`w-3 h-3 rounded-sm ${bgClass}`}></div><span className="text-sm">{status}</span></div>
                                {statusFilter === status ? <Check size={14} className="text-blue-400" /> : null}
                              </div>
                             )
                          })}
                          {statusFilter ? <div className="px-3 py-1.5 mt-1 hover:bg-slate-700 cursor-pointer text-slate-400 text-xs text-center border-t border-slate-700" onClick={() => {setStatusFilter(null); setIsFilterMenuOpen(false);}}>Limpar filtro</div> : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                  <div className="relative">
                    <button onClick={() => setIsSortMenuOpen(!isSortMenuOpen)} className={`p-1.5 rounded flex items-center gap-1 transition-colors ${sortConfig.field ? 'bg-blue-600/20 text-blue-400 border border-blue-500/50' : 'hover:bg-slate-800'}`}><ArrowUpDown size={14}/> Ordenar</button>
                    {isSortMenuOpen ? (
                      <>
                        <div className="fixed inset-0 z-30" onClick={() => setIsSortMenuOpen(false)}></div>
                        <div className="absolute top-full right-0 mt-1 w-56 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl py-2 z-40 animate-in fade-in zoom-in-95 duration-100">
                          <div className="px-3 pb-2 text-xs font-semibold text-slate-400 border-b border-slate-700 mb-1">Ordenar Tabela</div>
                          <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200 text-sm" onClick={() => {setSortConfig({field: 'elemento', order: 'asc'}); setIsSortMenuOpen(false);}}><span>Nome do Elemento (A-Z)</span>{sortConfig.field === 'elemento' && sortConfig.order === 'asc' ? <Check size={14} className="text-blue-400" /> : null}</div>
                          <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200 text-sm" onClick={() => {setSortConfig({field: 'elemento', order: 'desc'}); setIsSortMenuOpen(false);}}><span>Nome do Elemento (Z-A)</span>{sortConfig.field === 'elemento' && sortConfig.order === 'desc' ? <Check size={14} className="text-blue-400" /> : null}</div>
                          <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200 text-sm" onClick={() => {setSortConfig({field: 'priority', order: 'asc'}); setIsSortMenuOpen(false);}}><span>Prioridade</span>{sortConfig.field === 'priority' ? <Check size={14} className="text-blue-400" /> : null}</div>
                          <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200 text-sm" onClick={() => {setSortConfig({field: 'status', order: 'asc'}); setIsSortMenuOpen(false);}}><span>Status</span>{sortConfig.field === 'status' ? <Check size={14} className="text-blue-400" /> : null}</div>
                          <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200 text-sm" onClick={() => {setSortConfig({field: 'entrega', order: 'asc'}); setIsSortMenuOpen(false);}}><span>Data de Entrega (Mais próxima)</span>{sortConfig.field === 'entrega' && sortConfig.order === 'asc' ? <Check size={14} className="text-blue-400" /> : null}</div>
                          <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer flex items-center justify-between text-slate-200 text-sm" onClick={() => {setSortConfig({field: 'entrega', order: 'desc'}); setIsSortMenuOpen(false);}}><span>Data de Entrega (Mais distante)</span>{sortConfig.field === 'entrega' && sortConfig.order === 'desc' ? <Check size={14} className="text-blue-400" /> : null}</div>
                          {sortConfig.field ? <div className="px-3 py-1.5 mt-1 hover:bg-slate-700 cursor-pointer text-red-400 hover:text-red-300 text-xs text-center border-t border-slate-700" onClick={() => {setSortConfig({field: null, order: 'asc'}); setIsSortMenuOpen(false);}}>Limpar Ordenação</div> : null}
                        </div>
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col p-6 bg-[#11131c] overflow-hidden">
              <div className="flex-1 overflow-auto rounded-md">
                {activeTab === 'quadro' || activeTab === 'arquivadas' ? (
                  <div className="flex flex-col gap-10 min-w-max pb-16">
                    {categories.map(category => {
                      const categoryGroups = groups.filter(g => g.categoryId === category.id);
                      const isCatCollapsed = collapsedCategories.includes(category.id);
                      return (
                        <div key={category.id} className="flex flex-col mb-4">
                          <div className="flex items-center gap-3 mb-4 group/cat h-12 bg-slate-800/20 px-3 rounded-lg border border-slate-800 shadow-sm">
                            <div className="flex items-center justify-center cursor-pointer p-1 rounded hover:bg-slate-700 transition-colors" onClick={() => toggleCategoryCollapse(category.id)}>
                              {isCatCollapsed ? <ChevronRight size={22} className={category.colorClass} /> : <ChevronDown size={22} className={category.colorClass} />}
                            </div>
                            <FolderOpen size={20} className={`${category.colorClass} opacity-80`} />
                            <input type="text" value={category.title} onChange={(e) => updateCategoryTitle(category.id, e.target.value)} readOnly={currentUser?.role !== 'admin'} className={`text-xl font-bold bg-transparent border-none outline-none focus:bg-slate-800 rounded px-2 w-80 ${category.colorClass} ${currentUser?.role !== 'admin' ? 'cursor-default focus:bg-transparent' : ''}`} />
                            <span className="text-slate-500 text-sm ml-2 bg-slate-800 px-2 py-0.5 rounded-full">{categoryGroups.length} Grupos</span>
                            {(currentUser?.role === 'admin' || theme?.editorCanCreate) && activeTab !== 'arquivadas' && <button onClick={() => handleAddGroup(category.id)} className="ml-auto opacity-0 group-hover/cat:opacity-100 flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded text-slate-300 hover:text-white transition-all text-xs font-medium"><Plus size={14} /> Adicionar Grupo</button>}
                            {currentUser?.role === 'admin' ? <button onClick={() => handleDeleteCategory(category.id)} className="opacity-0 group-hover/cat:opacity-100 p-1.5 ml-2 mr-1 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded transition-colors" title="Excluir Categoria"><Trash2 size={16} /></button> : null}
                          </div>
                          {!isCatCollapsed ? (
                            <div className="pl-6 border-l-2 border-slate-800 ml-4 flex flex-col gap-8 pt-2">
                              {categoryGroups.map(group => {
                                const groupTasks = filteredTasks.filter(t => t.groupId === group.id);
                                const isCollapsed = collapsedGroups.includes(group.id);
                                const selectedInGroup = groupTasks.filter(t => selectedTasks.includes(t.id));
                                const allGroupSelected = groupTasks.length > 0 && selectedInGroup.length === groupTasks.length;
                                if (groupTasks.length === 0 && (personFilter || statusFilter || searchTerm || activeTab === 'arquivadas')) return null;

                                return (
                                  <div key={group.id} className="flex flex-col">
                                    <div className="flex items-center mb-2 group/header h-10 relative">
                                      <div className="relative flex items-center mr-2">
                                        <div className={`w-4 h-4 rounded-sm cursor-pointer opacity-0 group-hover/header:opacity-100 transition-all hover:scale-110 shadow-sm ${group.color.bg}`} onClick={() => setActiveColorPickerGroupId(activeColorPickerGroupId === group.id ? null : group.id)} title="Alterar cor do grupo" />
                                        {activeColorPickerGroupId === group.id ? (
                                          <>
                                            <div className="fixed inset-0 z-40" onClick={() => setActiveColorPickerGroupId(null)}></div>
                                            <div className="absolute top-6 left-0 z-50 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl p-3 flex gap-2 w-max max-w-[160px] flex-wrap animate-in fade-in zoom-in-95 duration-200">
                                              <div className="w-full text-xs text-slate-400 font-medium mb-1">Cor do grupo</div>
                                              {GROUP_COLORS.map(c => <div key={c.id} className={`w-5 h-5 rounded-full cursor-pointer hover:scale-110 transition-transform ${c.bg} ${group.color.id === c.id ? 'ring-2 ring-white ring-offset-2 ring-offset-[#1c1f2b]' : ''}`} onClick={() => updateGroupColor(group.id, c)} title="Selecionar cor" />)}
                                            </div>
                                          </>
                                        ) : null}
                                      </div>
                                      <div className="flex items-center gap-2 cursor-pointer" onClick={() => toggleGroupCollapse(group.id)}>
                                        {isCollapsed ? <ChevronRight size={20} className={group.color.text} /> : <ChevronDown size={20} className={group.color.text} />}
                                      </div>
                                      <input type="text" value={group.title} onClick={(e) => e.stopPropagation()} onChange={(e) => updateGroupTitle(group.id, e.target.value)} readOnly={currentUser?.role !== 'admin'} className={`text-lg font-semibold bg-transparent border-none outline-none focus:bg-slate-800 rounded px-1 ml-1 ${group.color.text} ${currentUser?.role !== 'admin' ? 'cursor-default focus:bg-transparent' : ''}`} />
                                      <span className="text-slate-500 text-sm ml-4">{groupTasks.length} Elementos</span>
                                      
                                      <div className="ml-auto w-48 flex flex-col items-center justify-center border-l border-slate-700 h-full shrink-0">
                                         <span className="text-[10px] text-slate-400 mb-1">Status</span>
                                         <div className="w-3/4 flex h-2 rounded-sm overflow-hidden bg-slate-800">
                                            {Object.entries(groupTasks.reduce((acc, t) => { acc[t.status] = (acc[t.status] || 0) + 1; return acc; }, {})).map(([status, count]) => {
                                              if (status === 'VAZIO') return null;
                                              const width = `${(count / groupTasks.length) * 100}%`;
                                              const bgClass = statusOptions[status]?.split(' ')[0] || 'bg-slate-700';
                                              return <div key={status} style={{ width }} className={bgClass} title={`${status}: ${count}`}></div>
                                            })}
                                         </div>
                                      </div>
                                      {currentUser?.role === 'admin' ? <button onClick={() => handleDeleteGroup(group.id)} className="opacity-0 group-hover/header:opacity-100 p-1.5 ml-2 mr-2 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded transition-colors shrink-0" title="Excluir Grupo"><Trash2 size={16} /></button> : null}
                                    </div>

                                    {!isCollapsed ? (
                                      <div className="border border-slate-700 rounded-t-md rounded-b-md bg-[#181b26] flex flex-col w-max min-w-full shadow-md">
                                        <div className="flex items-center border-b border-slate-700 text-xs font-semibold text-slate-400 bg-slate-800/50 sticky top-0 z-10 w-full select-none">
                                          <div className={`w-10 shrink-0 p-2 flex justify-center border-r border-slate-700 border-l-4 ${group.color.border} sticky left-0 z-[5] bg-slate-800/50`}>
                                            <input type="checkbox" className="rounded border-slate-600 bg-slate-800 cursor-pointer" checked={allGroupSelected} onChange={() => { if (allGroupSelected) { setSelectedTasks(selectedTasks.filter(id => !groupTasks.find(t => t.id === id))); } else { const newSelected = [...selectedTasks]; groupTasks.forEach(t => { if (!newSelected.includes(t.id)) newSelected.push(t.id); }); setSelectedTasks(newSelected); } }} />
                                          </div>
                                          <div className="w-80 shrink-0 p-2 border-r border-slate-700 sticky left-[40px] z-[5] bg-slate-800/50 relative">Elemento<div className="absolute top-0 -right-[6px] bottom-0 w-[6px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none z-[6]"></div></div>
                                          {columns.filter(col => !hiddenColumns.includes(col.id)).map(col => (
                                            <div key={col.id} style={{ width: col.width }} className={`relative shrink-0 p-2 border-r border-slate-700 text-center flex items-center justify-between ${currentUser?.role === 'admin' ? 'cursor-grab active:cursor-grabbing' : ''} group/col hover:bg-slate-700/30 transition-colors`} draggable={currentUser?.role === 'admin'} onDragStart={(e) => handleColDragStart(e, col.id)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => handleColDrop(e, col.id)}>
                                              {editingColId === col.id && editingColMenuKey === `${group.id}::${col.id}` ? (
                                                <input
                                                  type="text"
                                                  autoFocus
                                                  value={editingColTitle}
                                                  onClick={(e) => e.stopPropagation()}
                                                  onChange={(e) => setEditingColTitle(e.target.value)}
                                                  onBlur={confirmColumnRename}
                                                  onKeyDown={(e) => {
                                                    if (e.key === 'Enter') confirmColumnRename();
                                                    if (e.key === 'Escape') cancelColumnRename();
                                                  }}
                                                  className="w-full px-2 py-1 rounded bg-slate-900 border border-blue-500 text-slate-100 text-xs outline-none"
                                                />
                                              ) : (
                                                <span className="truncate px-2 flex-1">{col.title}</span>
                                              )}
                                              {currentUser?.role === 'admin' && (
                                              <button data-col-trigger onClick={(e) => { e.stopPropagation(); const menuKey = `${group.id}::${col.id}`; setColMenuOpen(colMenuOpen === menuKey ? null : menuKey); }} className="opacity-0 group-hover/col:opacity-100 p-1 hover:bg-slate-600 rounded transition-all">
                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>
                                              </button>
                                              )}
                                              {colMenuOpen === `${group.id}::${col.id}` && currentUser?.role === 'admin' && (
                                                <div data-col-menu className="absolute top-full right-0 mt-1 w-60 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl z-50 py-1 animate-in fade-in zoom-in-95 duration-100">
                                                  <button onClick={() => startColumnRename(group.id, col)} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 flex items-center gap-2">
                                                    <Pencil size={12} />
                                                    Renomear coluna
                                                  </button>
                                                  <button onClick={() => handleDuplicateColumn(col.id)} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 flex items-center gap-2">
                                                    <Copy size={12} />
                                                    Duplicar coluna
                                                  </button>
                                                  <button onClick={() => handleAddColumnRight(col.id, col.type, `${col.title} (nova)`)} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 flex items-center gap-2">
                                                    <Plus size={12} />
                                                    Adicionar coluna a direita
                                                  </button>
                                                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wide text-slate-500 border-t border-slate-700 mt-1">
                                                    Alterar tipo de coluna
                                                  </div>
                                                  <div className="grid grid-cols-2 gap-1 px-2 pb-2">
                                                    <button onClick={() => handleChangeColumnType(col.id, 'text')} className="px-2 py-1 text-[11px] rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-left">Texto</button>
                                                    <button onClick={() => handleChangeColumnType(col.id, 'date')} className="px-2 py-1 text-[11px] rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-left">Data</button>
                                                    <button onClick={() => handleChangeColumnType(col.id, 'numbers')} className="px-2 py-1 text-[11px] rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-left">Numeros</button>
                                                    <button onClick={() => handleChangeColumnType(col.id, 'people')} className="px-2 py-1 text-[11px] rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-left">Pessoas</button>
                                                    <button onClick={() => handleChangeColumnType(col.id, 'status')} className="px-2 py-1 text-[11px] rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-left">Status</button>
                                                    <button onClick={() => handleChangeColumnType(col.id, 'priority')} className="px-2 py-1 text-[11px] rounded bg-slate-800 hover:bg-slate-700 text-slate-200 text-left">Prioridade</button>
                                                  </div>
                                                  <div className="border-t border-slate-700" />
                                                  <button onClick={() => toggleColumnVisibility(col.id)} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 flex items-center gap-2">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>
                                                    Ocultar coluna
                                                  </button>
                                                  <button onClick={() => handleDeleteColumn(col.id)} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 text-red-400 flex items-center gap-2">
                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>
                                                    Excluir coluna
                                                  </button>
                                                </div>
                                              )}
                                              {currentUser?.role === 'admin' && <div className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-blue-500 z-10" onMouseDown={(e) => { e.stopPropagation(); setResizingCol({ id: col.id, startX: e.clientX, startWidth: col.width }); }} />}
                                            </div>
                                          ))}
                                          {hiddenColumns.length > 0 && currentUser?.role === 'admin' && (
                                            <div className="relative shrink-0 flex items-center gap-1 px-2 border-r border-slate-700 h-full">
                                              <div className="relative group/hidden">
                                                <button className="flex items-center gap-1 px-2 py-1 text-xs text-amber-400 hover:bg-amber-500/10 rounded transition-colors">
                                                  <EyeOff size={12} />
                                                  <span>{hiddenColumns.length}</span>
                                                </button>
                                                <div className="absolute top-full right-0 mt-1 w-48 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl z-50 py-1 hidden group-hover/hidden:block animate-in fade-in zoom-in-95 duration-100">
                                                  <div className="px-3 py-1.5 text-[10px] text-slate-500 font-semibold uppercase">Colunas Ocultas</div>
                                                  {hiddenColumns.map(colId => {
                                                    const col = columns.find(c => c.id === colId);
                                                    if (!col) return null;
                                                    return (
                                                      <button key={colId} onClick={() => unhideColumn(colId)} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 flex items-center justify-between gap-2">
                                                        <span className="text-slate-300">{col.title}</span>
                                                        <Eye size={12} className="text-emerald-400" />
                                                      </button>
                                                    );
                                                  })}
                                                  <div className="border-t border-slate-700 mt-1 pt-1">
                                                    <button onClick={showAllColumns} className="w-full px-3 py-2 text-left text-xs hover:bg-slate-700 text-blue-400 flex items-center gap-2">
                                                      Mostrar todas
                                                    </button>
                                                  </div>
                                                </div>
                                              </div>
                                            </div>
                                          )}
                                          {currentUser?.role === 'admin' && (
                                          <div data-add-col-trigger className="relative w-12 shrink-0 flex items-center justify-center border-r border-slate-700 hover:bg-slate-700 cursor-pointer h-full" onClick={() => setAddColMenuOpen(prev => prev === group.id ? null : group.id)}>
                                            <Plus size={16} />
                                            {addColMenuOpen === group.id ? <ColumnMenuDropdown onClose={() => setAddColMenuOpen(null)} onAdd={handleAddNewColumn} /> : null}
                                          </div>
                                          )}
                                        </div>

                                        {groupTasks.map((task) => {
                                          const isSelected = selectedTasks.includes(task.id);
                                          const hasUpdates = task.updates && task.updates.length > 0;
                                          return (
                                          <div key={task.id} className={`flex items-center border-b border-slate-700/50 group transition-colors w-full ${isSelected ? 'bg-blue-900/20' : 'hover:bg-slate-800/50'}`}>
                                          <div className={`w-10 shrink-0 p-2 flex justify-center border-r border-slate-700 border-l-4 ${isSelected ? 'border-l-blue-500' : group.color.border} sticky left-0 z-[3] bg-[#181b26]`}>
                                            <input type="checkbox" className={`rounded border-slate-600 bg-slate-800 cursor-pointer transition-opacity ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} checked={isSelected} onChange={() => toggleTaskSelection(task.id)} />
                                          </div>
                                          <div className="w-80 shrink-0 p-2 border-r border-slate-700 flex items-center gap-2 sticky left-[40px] z-[3] bg-[#181b26] relative"><div className="absolute top-0 -right-[6px] bottom-0 w-[6px] bg-gradient-to-r from-black/30 to-transparent pointer-events-none z-[4]"></div>
                                            <div className="relative w-full">
                                              <EditableTextInput type="text" value={task.elemento} onCommit={(nextValue) => updateTask(task.id, 'elemento', nextValue)} className="bg-transparent border-none outline-none w-full text-slate-200 focus:bg-slate-800 px-1 py-0.5 rounded" />
                                            </div>
                                            <div className={`relative flex items-center justify-center w-7 h-7 rounded hover:bg-slate-700 cursor-pointer shrink-0 transition-opacity ${hasUpdates ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} onClick={() => setActivePanelTaskId(task.id)} title="Atualizações e Notas">
                                              <MessageSquare size={16} className={`${hasUpdates ? 'text-blue-400 fill-blue-400/20' : 'text-slate-400 hover:text-blue-400'}`} />
                                              {hasUpdates ? <span className="absolute -top-1 -right-1 flex h-3 w-3"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span><span className="relative inline-flex rounded-full h-3 w-3 bg-blue-500 border-2 border-[#181b26]"></span></span> : null}
                                            </div>
                                          </div>
                                          {columns.filter(col => !hiddenColumns.includes(col.id)).map(col => (
                                            <div key={col.id} style={{ width: col.width }} className="shrink-0 border-r border-slate-700 p-[1px] flex items-center justify-center h-full min-h-[36px]"><TaskCellContent task={task} col={col} updateTask={updateTask} statusOptions={statusOptions} taskStatusConfig={taskStatusConfig} handleAddStatusOption={handleAddStatusOption} currentUser={currentUser} handleOpenEditStatusModal={handleOpenEditStatusModal} members={members} getBrazilDate={getBrazilDate} /></div>
                                          ))}
                                          {hiddenColumns.length > 0 && currentUser?.role === 'admin' && (
                                            <div className="shrink-0 px-2 border-r border-slate-700 h-full"></div>
                                          )}
                                          {currentUser?.role === 'admin' && (
                                          <div className="w-12 shrink-0 border-r border-slate-700 h-full"></div>
                                          )}
                                      </div>
                                    )})}
                                    {(currentUser?.role === 'admin' || theme?.editorCanCreate) && activeTab !== 'arquivadas' && (
                                    <div className="flex items-center border-t border-slate-700/50 p-2 text-slate-500 hover:bg-slate-800/30 cursor-pointer transition-colors" onClick={() => handleAddTask(group.id)}>
                                      <div className={`w-10 flex justify-center border-l-4 border-transparent shrink-0 sticky left-0 z-[3] bg-[#181b26]`}></div><div className="sticky left-[40px] z-[3] bg-[#181b26]"><Plus size={16} className="mr-2 inline" /><span>Adicionar elemento</span></div>
                                    </div>
                                    )}
                                  </div>
                                  ) : null}
                                </div>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                    <div className="mt-4 pt-4 border-t border-slate-800">
                      {(currentUser?.role === 'admin' || theme?.editorCanCreate) && activeTab !== 'arquivadas' && <button onClick={handleAddCategory} className="flex items-center gap-2 px-5 py-2.5 bg-[#181b26] border border-slate-600 rounded-lg text-slate-300 hover:bg-slate-700 hover:text-white transition-all shadow-sm font-medium"><FolderOpen size={18} /> Adicionar Nova Categoria</button>}
                    </div>
                    <DashboardSummary tasks={filteredTasks} statusOptions={statusOptions} currentUser={currentUser} />
                  </div>
                ) : activeTab === 'kanban' ? (
                  <div className="flex flex-col min-h-full w-full pb-16">
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <h2 className="text-xl font-bold">Kanban</h2>
                      <button
                        onClick={() => setShowSlaReport(true)}
                        className="px-3 py-2 rounded-lg bg-red-500/15 hover:bg-red-500/25 border border-red-500/40 text-red-200 text-sm font-semibold flex items-center gap-2"
                        title="Ver tarefas que ultrapassaram o SLA"
                      >
                        <AlertTriangle size={16} /> Relatório SLA
                      </button>
                    </div>
                    <div className="flex-1 mb-4 overflow-hidden"><KanbanBoard tasks={filteredTasks} statusColors={statusOptions} updateTask={updateTask} taskStatusConfig={taskStatusConfig} allMembers={members} onOpenNotes={setActivePanelTaskId} canReorderStatuses={currentUser?.role === 'admin'} onReorderStatuses={handleReorderTaskStatuses} /></div>
                    <DashboardSummary tasks={filteredTasks} statusOptions={statusOptions} currentUser={currentUser} />
                  </div>
                ) : (
                  <div className="h-full w-full"><CalendarBoard tasks={filteredTasks} statusColors={statusOptions} onOpenNotes={setActivePanelTaskId} getBrazilDate={getBrazilDate} onCreateTask={handleCreateTaskFromCalendar} groups={groups} allMembers={members} /></div>
                )}
              </div>
            </div>

            {activePanelTaskId ? (
              <>
                <div className="absolute inset-0 bg-black/40 z-40 backdrop-blur-[1px] transition-opacity" onClick={() => setActivePanelTaskId(null)}></div>
                <div className="absolute top-0 right-0 h-full w-[600px] max-w-full bg-[#1c1f2b] border-l border-slate-700 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
                  <div className="px-6 py-5 border-b border-slate-700 bg-[#181b26] flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h2 className="text-2xl font-semibold text-white tracking-tight truncate">{activeTask?.elemento}</h2>
                      <div className="flex gap-6 mt-6 text-sm">
                        <span onClick={() => setActivePanelTab('atualizações')} className={`pb-2 cursor-pointer flex items-center gap-2 transition-colors ${activePanelTab === 'atualizações' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}><Home size={16}/> Atualizações</span>
                        <span onClick={() => setActivePanelTab('arquivos')} className={`pb-2 cursor-pointer flex items-center gap-2 transition-colors ${activePanelTab === 'arquivos' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}><Paperclip size={16}/> Arquivos</span>
                        <span onClick={() => setActivePanelTab('log')} className={`pb-2 cursor-pointer flex items-center gap-2 transition-colors ${activePanelTab === 'log' ? 'border-b-2 border-blue-500 text-white font-medium' : 'text-slate-400 hover:text-slate-200'}`}><Clock size={16}/> Log de atividade</span>
                      </div>
                    </div>
                    <button onClick={() => setActivePanelTaskId(null)} className="p-2 rounded-full hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"><X size={20} /></button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-6 bg-[#11131c]">
                    {activePanelTab === 'atualizações' ? (
                      <>
                        <UpdatesInputBox onSend={(text, files) => handleAddUpdate(activeTask.id, text, files)} taskId={activeTask.id} taskFiles={activeTask?.files} taskUpdates={activeTask?.updates} taskName={activeTask?.elemento} aiModel={aiModel} members={members} />
                        <div className="mt-8 space-y-6">
                          {activeTask?.updates?.length > 0 ? (
                            activeTask.updates.map((update) => (
                              <div key={update.id} className="bg-[#181b26] border border-slate-700 rounded-lg p-5 shadow-sm">
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-500 to-blue-500 flex items-center justify-center text-white font-bold text-xs shadow-md">{update.author}</div><div><div className="font-semibold text-slate-200 text-sm">{update.authorName}</div></div></div>
                                  <div className="flex items-center gap-2">
                                    <button onClick={() => openWhatsAppShareModal(activeTask, update)} className="text-xs text-slate-500 hover:text-emerald-400 transition-colors p-1" title="Enviar nota no WhatsApp"><Send size={14} /></button>
                                    {(update.author === currentUser?.initials || currentUser?.role === 'admin') && editingUpdateId !== update.id && (
                                      <>
                                        <button onClick={() => { setEditingUpdateId(update.id); setEditingUpdateText(update.text); }} className="text-xs text-slate-500 hover:text-blue-400 transition-colors p-1" title="Editar atualização"><Pencil size={14} /></button>
                                        <button onClick={() => handleDeleteUpdate(activeTask.id, update.id)} className="text-xs text-slate-500 hover:text-red-400 transition-colors p-1" title="Excluir atualização"><Trash2 size={14} /></button>
                                      </>
                                    )}
                                    <div className="flex items-center gap-1 text-xs text-slate-500"><Clock size={14} /> {update.date}</div>
                                  </div>
                                </div>
                                {editingUpdateId === update.id ? (
                                  <div className="pl-11">
                                    <textarea value={editingUpdateText} onChange={(e) => setEditingUpdateText(e.target.value)} className="w-full bg-black/20 border border-slate-600 rounded-lg p-3 text-sm text-slate-200 outline-none focus:border-blue-500 resize-none min-h-[80px]" autoFocus />
                                    <div className="flex gap-2 mt-2 justify-end">
                                      <button onClick={() => { setEditingUpdateId(null); setEditingUpdateText(''); }} className="text-xs text-slate-400 hover:text-red-400 px-3 py-1.5 transition-colors">Cancelar</button>
                                      <button onClick={() => handleEditUpdate(activeTask.id, update.id, editingUpdateText)} disabled={!editingUpdateText.trim()} className={`text-xs px-3 py-1.5 rounded font-medium transition-colors ${editingUpdateText.trim() ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-slate-700 text-slate-500 cursor-not-allowed'}`}>Salvar</button>
                                    </div>
                                  </div>
                                ) : (
                                  <div className="text-slate-300 text-sm pl-11 leading-relaxed whitespace-pre-line">{update.text}</div>
                                )}
                                {update.files && update.files.length > 0 && (
                                  <div className="pl-11 mt-3 flex flex-wrap gap-2">
                                    {update.files.map(f => (
                                      f.type?.startsWith('image') ? (
                                        <button key={f.id} onClick={() => setLightboxImage(api.getFileUrl(f.id))} className="block">
                                          <img src={api.getFileUrl(f.id)} alt={f.name} className="w-48 h-48 object-cover rounded-lg border border-slate-600 hover:border-blue-500 hover:shadow-lg hover:scale-105 transition-all cursor-pointer" />
                                        </button>
                                      ) : (
                                        <a key={f.id} href={api.getFileUrl(f.id)} target="_blank" rel="noopener noreferrer" className="block">
                                          <div className="flex items-center gap-1.5 bg-slate-800 border border-slate-600 rounded px-2 py-1 text-xs text-slate-300 hover:border-blue-500 transition-colors"><FileText size={12} />{f.name}</div>
                                        </a>
                                      )
                                    ))}
                                  </div>
                                )}
                                {update.replies && update.replies.length > 0 && (
                                  <div className="pl-11 mt-3 space-y-2">
                                    {update.replies.map(r => (
                                      <div key={r.id} className="bg-black/20 rounded p-3 text-sm">
                                        <div className="flex items-center gap-2 mb-1"><span className="font-semibold text-slate-300 text-xs">{r.authorName}</span><span className="text-[10px] text-slate-500">{r.date}</span></div>
                                        <div className="text-slate-400 text-xs">{r.text}</div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                                {replyingTo === update.id && (
                                  <div className="pl-11 mt-3 flex gap-2">
                                    <input type="text" placeholder="Escreva uma resposta..." className="flex-1 bg-black/20 border border-slate-600 rounded px-3 py-1.5 text-sm text-slate-200 outline-none focus:border-blue-500" onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) handleReplyUpdate(activeTask.id, update.id, e.target.value); }} autoFocus />
                                    <button onClick={() => setReplyingTo(null)} className="text-xs text-slate-500 hover:text-red-400 px-2">Cancelar</button>
                                  </div>
                                )}
                                <div className="mt-4 pl-11 flex gap-3">
                                  <button onClick={() => handleLikeUpdate(activeTask.id, update.id)} className={`text-xs font-medium flex items-center gap-1 transition-colors ${(update.likes || []).includes(currentUser?.initials) ? 'text-blue-400' : 'text-slate-400 hover:text-blue-400'}`}><Smile size={14} /> Gostar{(update.likes || []).length > 0 && <span className="ml-0.5">({update.likes.length})</span>}</button>
                                  <button onClick={() => setReplyingTo(replyingTo === update.id ? null : update.id)} className={`text-xs font-medium flex items-center gap-1 transition-colors ${replyingTo === update.id ? 'text-blue-400' : 'text-slate-400 hover:text-blue-400'}`}><MessageSquare size={14} /> Responder</button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <div className="text-center py-10 flex flex-col items-center justify-center"><div className="w-16 h-16 bg-slate-800 rounded-full flex items-center justify-center mb-4 text-slate-600"><MessageSquare size={32} /></div><h3 className="text-slate-300 font-medium text-lg">Ainda não há atualizações</h3><p className="text-slate-500 text-sm mt-1 max-w-xs">Escreva uma atualização acima para notificar a equipa sobre o progresso desta tarefa.</p></div>
                          )}
                        </div>
                      </>
                    ) : null}
                    {activePanelTab === 'log' ? (
                      <div className="text-slate-200 animate-in fade-in duration-200">
                        <div className="flex items-center justify-between mb-4 px-2"><span className="text-sm font-medium">Outras atividades</span><span className="text-sm text-blue-400 cursor-pointer hover:underline">Atividade da automação</span></div>
                        <div className="flex items-center gap-4 mb-6"><button className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded flex items-center gap-2 text-sm font-medium transition-colors shadow-sm">Log do filtro <ChevronDown size={14} /></button><button className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"><User size={14} /> Pessoa</button><button className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors"><Sparkles size={14} /> Alimentado por IA</button></div>
                        <div className="border border-slate-700 rounded-md bg-[#181b26] overflow-hidden shadow-sm">
                          {activeTask?.activityLog?.length > 0 ? (
                            activeTask.activityLog.map((log, index) => (
                              <div key={log.id} className={`flex items-center p-3 hover:bg-slate-800/50 transition-colors ${index !== activeTask.activityLog.length - 1 ? 'border-b border-slate-700/50' : ''}`}>
                                <div className="w-16 flex items-center gap-1.5 text-xs text-slate-400 shrink-0"><Clock size={12} /> {log.time}</div>
                                <div className="w-48 flex items-center gap-2 shrink-0"><div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-[10px] shadow-sm">{log.authorInitials}</div><span className="text-sm truncate pr-2 text-slate-300">{log.taskName}</span></div>
                                <div className="w-32 flex items-center gap-2 text-sm text-slate-300 shrink-0">{log.field === 'Created' ? <PlusCircle size={14} className="text-slate-500"/> : log.field === 'Equipe' ? <UserPlus size={14} className="text-slate-500"/> : <Settings size={14} className="text-slate-500"/>}{log.field}</div>
                                <div className="flex-1 text-sm font-medium text-slate-200 truncate pr-4">{log.details}</div>
                                <button className="px-3 py-1 border border-slate-600 hover:bg-slate-700 rounded text-xs font-medium text-slate-300 transition-colors shrink-0 flex items-center gap-1">Desfazer</button>
                              </div>
                            ))
                          ) : <div className="p-8 text-center text-slate-500 text-sm">Nenhuma atividade registada ainda.</div>}
                        </div>
                      </div>
                    ) : null}
                    {activePanelTab === 'arquivos' ? (
                      <div className="text-slate-200 animate-in fade-in duration-200">
                        <input type="file" id="file-upload-input" className="hidden" multiple onChange={async (e) => {
                          if (!activeTask?.id) return;
                          const files = Array.from(e.target.files);
                          for (const file of files) {
                            const compressed = await compressImage(file);
                            api.uploadFile(activeTask.id, compressed).then(result => {
                              if (result.file) {
                                const newFile = { id: result.file.id, name: result.file.name, size: result.file.size, type: result.file.type, taskId: activeTask.id };
                                const updatedTasks = tasks.map(t => t.id === activeTask.id ? { ...t, files: [...(t.files || []), newFile] } : t);
                                setTasks(updatedTasks);
                                saveStateToLocalStorage({ tasks: updatedTasks });
                              }
                            }).catch(err => addToast('Erro no upload', err.message, 'error'));
                          }
                          e.target.value = '';
                        }} />
                        <div className="border-2 border-dashed border-slate-700 rounded-lg p-10 flex flex-col items-center justify-center text-center bg-[#181b26]/50 mb-6 hover:bg-[#181b26] hover:border-blue-500 transition-colors cursor-pointer group" onClick={() => document.getElementById('file-upload-input').click()} onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-blue-500', 'bg-[#181b26]'); }} onDragLeave={(e) => { e.currentTarget.classList.remove('border-blue-500', 'bg-[#181b26]'); }} onDrop={async (e) => { e.preventDefault(); e.currentTarget.classList.remove('border-blue-500', 'bg-[#181b26]'); if (!activeTask?.id) return; const files = Array.from(e.dataTransfer.files); for (const file of files) { const compressed = await compressImage(file); api.uploadFile(activeTask.id, compressed).then(result => { if (result.file) { const newFile = { id: result.file.id, name: result.file.name, size: result.file.size, type: result.file.type, taskId: activeTask.id }; const updatedTasks = tasks.map(t => t.id === activeTask.id ? { ...t, files: [...(t.files || []), newFile] } : t); setTasks(updatedTasks); saveStateToLocalStorage({ tasks: updatedTasks }); } }).catch(err => addToast('Erro no upload', err.message, 'error')); } }}><div className="w-12 h-12 bg-slate-800 group-hover:bg-blue-600/20 rounded-full flex items-center justify-center text-slate-400 group-hover:text-blue-400 mb-3 transition-colors"><Plus size={24} /></div><h3 className="text-base font-medium mb-1 group-hover:text-white transition-colors">Adicionar arquivos</h3><p className="text-sm text-slate-500">Arraste e solte arquivos aqui ou clique para selecionar.</p></div>
                        {activeTask?.files?.length > 0 ? (
                          <div className="grid grid-cols-2 gap-4">
                            {activeTask.files.map(file => (
                              <div key={file.id} className="border border-slate-700 rounded-md p-3 flex items-start gap-3 bg-[#181b26] hover:border-blue-500 transition-colors group shadow-sm relative">
                                <a href={api.getFileUrl(file.id)} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 flex-1 min-w-0">
                                  <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center text-blue-400 shrink-0">{file.type?.startsWith('image') ? <ImageIcon size={20} /> : <FileText size={20} />}</div>
                                  <div className="flex-1 overflow-hidden"><div className="text-sm font-medium truncate group-hover:text-blue-400 transition-colors">{file.name}</div><div className="text-xs text-slate-500 mt-0.5">{typeof file.size === 'number' ? formatFileSize(file.size) : file.size}</div></div>
                                </a>
                                <button onClick={() => { api.deleteFile(file.id).then(() => { const updatedTasks = tasks.map(t => t.id === activeTask.id ? { ...t, files: (t.files || []).filter(f => f.id !== file.id) } : t); setTasks(updatedTasks); saveStateToLocalStorage({ tasks: updatedTasks }); }).catch(err => addToast('Erro ao excluir', err.message, 'error')); }} className="absolute top-1 right-1 p-1 text-slate-600 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100" title="Excluir arquivo"><X size={14} /></button>
                              </div>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                </div>
              </>
            ) : null}
          </>
        ) : currentRoute === 'my_work' ? (
          <MyWorkScreen />
        ) : currentRoute === 'albuns' ? (
          <AlbumsScreen />
        ) : currentRoute === 'team_management' ? (
          <TeamManagementScreen />
        ) : currentRoute === 'clients' && currentUser?.role === 'admin' ? (
          <ClientsScreen onOpenClient={(id) => { setClientDetailId(id); setCurrentRoute('client_detail'); }} />
        ) : currentRoute === 'client_detail' && currentUser?.role === 'admin' ? (
          <ClientDetailScreen
            clientId={clientDetailId}
            onBack={() => { setClientDetailId(null); setCurrentRoute('clients'); }}
          />
        ) : currentRoute === 'social' ? (
          <SocialPostScreen currentUser={currentUser} />
        ) : currentRoute === 'locacoes' && currentUser?.role === 'admin' ? (
          <EquipamentosLocacaoScreen />
        ) : currentRoute === 'notification_center' && currentUser?.role === 'admin' ? (
          <NotificationsCenterScreen />
        ) : currentRoute === 'message_hub' && currentUser?.role === 'admin' ? (
          <MessageHubScreen />
        ) : currentRoute === 'sales_agent' && currentUser?.role === 'admin' ? (
          <SalesAgentScreen />
        ) : currentRoute === 'settings' && currentUser?.role === 'admin' ? (
          <SettingsScreen aiModel={aiModel} setAiModel={(m) => { setAiModel(m); localStorage.setItem('pf_ai_model', m); }} />
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center p-6" style={{ backgroundColor: theme.primaryBg }}>
            <div className="w-24 h-24 bg-slate-800 rounded-full flex items-center justify-center mb-6 text-slate-600 shadow-inner">
              <CheckSquare size={40} />
            </div>
            <h2 className="text-2xl font-semibold text-slate-300 mb-2">
              Página em construção
            </h2>
            <p className="text-slate-500 max-w-md text-center">
              Esta área de trabalho está pronta para ser estruturada. Clique em "Gestão de Tarefas" no menu lateral para voltar ao quadro principal.
            </p>
            <button onClick={() => setCurrentRoute('gestao_photofine')} className="mt-8 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors shadow-md flex items-center gap-2"><LayoutGrid size={18} /> Voltar para Gestão de Tarefas</button>
          </div>
        )}
      </main>

      {showSlaReport ? (
        <SlaReportModal
          onClose={() => setShowSlaReport(false)}
          onOpenTask={(item) => {
            setCurrentRoute('gestao_photofine');
            setActiveTab('kanban');
            setActivePanelTaskId(item.id);
            setShowSlaReport(false);
          }}
        />
      ) : null}

      {showBuildInfoModal ? (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[130] flex items-center justify-center p-4" onClick={() => setShowBuildInfoModal(false)}>
          <div className="w-full max-w-lg border border-slate-700 rounded-xl shadow-2xl overflow-hidden" style={{ backgroundColor: 'rgba(28, 31, 43, 1)' }} onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-slate-700 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold">Informacoes do build</h3>
                <p className="text-xs opacity-60 mt-0.5">Use para conferir se o deploy carregou o commit esperado.</p>
              </div>
              <button onClick={() => setShowBuildInfoModal(false)} className="text-slate-400 hover:text-white"><X size={20} /></button>
            </div>
            <div className="p-5 text-xs font-mono space-y-3">
              <div>
                <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Frontend (este navegador)</div>
                <div>sha: <span className="text-emerald-300">{buildInfo?.sha || '-'}</span></div>
                <div>short: <span className="text-emerald-300">{buildInfo?.shortSha || '-'}</span>{buildInfo?.isDirty === '1' ? <span className="text-amber-300"> (dirty*)</span> : null}</div>
                <div>branch: {buildInfo?.branch || '-'}</div>
                <div>commit: {buildInfo?.commitDate ? new Date(buildInfo.commitDate).toLocaleString('pt-BR') : '-'}</div>
                <div>msg: <span className="opacity-80 break-all">{buildInfo?.commitMessage || '-'}</span></div>
                <div>build (UTC): {buildInfo?.buildDate ? new Date(buildInfo.buildDate).toLocaleString('pt-BR') : '-'}</div>
                <div>node: {buildInfo?.nodeVersion || '-'}</div>
              </div>
              <div className="pt-3 border-t border-slate-700">
                <div className="text-slate-400 text-[10px] uppercase tracking-wider mb-1">Backend (servidor)</div>
                {serverBuildInfo ? (
                  <>
                    <div>sha: <span className="text-emerald-300">{serverBuildInfo.sha || '-'}</span></div>
                    <div>short: <span className="text-emerald-300">{serverBuildInfo.shortSha || '-'}</span>{serverBuildInfo.isDirty === '1' ? <span className="text-amber-300"> (dirty*)</span> : null}</div>
                    <div>branch: {serverBuildInfo.branch || '-'}</div>
                    <div>commit: {serverBuildInfo.commitDate ? new Date(serverBuildInfo.commitDate).toLocaleString('pt-BR') : '-'}</div>
                    <div>msg: <span className="opacity-80 break-all">{serverBuildInfo.commitMessage || '-'}</span></div>
                    <div>iniciado: {serverBuildInfo.serverStartTime ? new Date(serverBuildInfo.serverStartTime).toLocaleString('pt-BR') : '-'} ({Math.floor((serverBuildInfo.uptimeSeconds || 0) / 60)} min)</div>
                    <div>node: {serverBuildInfo.nodeVersion || '-'}</div>
                    <div>plataforma: {serverBuildInfo.platform || '-'}/{serverBuildInfo.arch || '-'}</div>
                    <div>build dir: {serverBuildInfo.buildDirExists ? 'presente' : 'AUSENTE'}</div>
                  </>
                ) : (
                  <div className="opacity-60">Nao foi possivel obter /api/build-info.</div>
                )}
              </div>
              <div className="pt-3 border-t border-slate-700 text-[10px] opacity-60">
                {serverBuildInfo?.shortSha && buildInfo?.shortSha && serverBuildInfo.shortSha === buildInfo.shortSha
                  ? 'Frontend e backend estao no mesmo commit. Se voce esperava outro, faca hard-refresh (Ctrl+Shift+R) e limpe o cache do navegador/CDN.'
                  : 'Frontend e backend em commits diferentes - verifique se fez deploy de ambos.'}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {whatsAppShareModal ? (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={closeWhatsAppShareModal}>
          <div className="bg-[#1c1f2b] border border-slate-700 rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <Send size={18} className="text-emerald-400" />
              Enviar nota no WhatsApp
            </h3>
            <p className="text-sm text-slate-400 mb-5">
              Escolha se a mensagem da nota será enviada para cliente ou para um membro da equipe.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
              <button
                onClick={() => setWhatsAppShareTarget('client')}
                className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                  whatsAppShareTarget === 'client'
                    ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                    : 'border-slate-600 text-slate-300 hover:border-slate-500'
                }`}
              >
                Cliente
              </button>
              <button
                onClick={() => setWhatsAppShareTarget('team')}
                className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                  whatsAppShareTarget === 'team'
                    ? 'border-blue-500 bg-blue-500/20 text-blue-300'
                    : 'border-slate-600 text-slate-300 hover:border-slate-500'
                }`}
              >
                Membro da equipe
              </button>
            </div>

            {whatsAppShareTarget === 'client' ? (
              <div className="mb-4 rounded-lg border border-slate-700 bg-black/20 p-3">
                {whatsAppShareClientColumn ? (
                  whatsAppShareClientPhone ? (
                    <p className="text-sm text-emerald-300">Numero encontrado na coluna "Whatsapp Cliente": {whatsAppShareClientPhone}</p>
                  ) : (
                    <p className="text-sm text-amber-300">A coluna "Whatsapp Cliente" existe, mas essa tarefa ainda não tem número preenchido.</p>
                  )
                ) : (
                  <p className="text-sm text-amber-300">Não encontramos a coluna "Whatsapp Cliente" nessa tabela.</p>
                )}
              </div>
            ) : (
              <div className="mb-4">
                <label className="block text-xs text-slate-400 mb-1">Selecionar membro da equipe</label>
                <select
                  value={whatsAppShareMember}
                  onChange={(e) => setWhatsAppShareMember(e.target.value)}
                  className="w-full bg-black/20 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500"
                >
                  <option value="">Selecione um membro</option>
                  {whatsAppShareTeamMembers.map((member) => (
                    <option key={member.id} value={member.initials}>
                      {member.name} ({member.initials}) - {member.whatsappDigits}
                    </option>
                  ))}
                </select>
                {whatsAppShareTeamMembers.length === 0 ? (
                  <p className="text-xs text-amber-300 mt-2">Nenhum membro atribuido com WhatsApp cadastrado.</p>
                ) : null}
              </div>
            )}

            <div className="rounded-lg border border-slate-700 bg-black/20 p-3 mb-5">
              <p className="text-xs text-slate-400 mb-1">Mensagem da nota</p>
              <p className="text-sm text-slate-200 whitespace-pre-line">{whatsAppShareModal.text}</p>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={closeWhatsAppShareModal} className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700/50 transition-colors">
                Cancelar
              </button>
              <button
                onClick={handleSendUpdateToWhatsApp}
                disabled={isSendingWhatsAppNote || (whatsAppShareTarget === 'client' ? !whatsAppShareClientPhone : !whatsAppShareMember)}
                className={`px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2 ${
                  (isSendingWhatsAppNote || (whatsAppShareTarget === 'client' ? !whatsAppShareClientPhone : !whatsAppShareMember))
                    ? 'bg-slate-700 cursor-not-allowed text-slate-500'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                <Send size={14} />
                {isSendingWhatsAppNote ? 'Enviando...' : 'Enviar no WhatsApp'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {whatsAppNotifyModal ? (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[120] flex items-center justify-center p-4" onClick={closeWhatsAppNotifyModal}>
          <div className="bg-[#1c1f2b] border border-slate-700 rounded-xl shadow-2xl w-full max-w-xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
              <MessageSquare size={18} className="text-emerald-400" />
              Notificar tarefa por WhatsApp
            </h3>
            <p className="text-sm text-slate-400 mb-5">
              Envie a notificação para o WhatsApp do membro da equipe atribuído à tarefa.
            </p>

            <div className="mb-4 rounded-lg border border-slate-700 bg-black/20 p-3">
              <p className="text-xs text-slate-400 mb-1">Tarefa(s)</p>
              {whatsAppNotifyTasks.map((task) => (
                <p key={task.id} className="text-sm text-slate-200">• {task.elemento}</p>
              ))}
            </div>

            <div className="mb-4">
              <label className="block text-xs text-slate-400 mb-2">Membros a notificar</label>
              <div className="flex flex-wrap gap-2">
                {whatsAppNotifyMembers.map((member) => {
                  const isSelected = (whatsAppNotifyModal.selectedMemberInitials || []).includes(member.initials);
                  return (
                    <button
                      key={member.initials}
                      type="button"
                      onClick={() => toggleWhatsAppNotifyMember(member.initials)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                        isSelected
                          ? 'border-emerald-500 bg-emerald-500/20 text-emerald-300'
                          : 'border-slate-600 text-slate-300 hover:border-slate-500'
                      }`}
                    >
                      {member.name} ({member.initials}) - {member.whatsappDigits}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs text-slate-400 mb-2">Prioridade</label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setWhatsAppNotifyPriority('ALTA')}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                    whatsAppNotifyPriority === 'ALTA'
                      ? 'border-amber-500 bg-amber-500/20 text-amber-300'
                      : 'border-slate-600 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  ALTA
                </button>
                <button
                  type="button"
                  onClick={() => setWhatsAppNotifyPriority('URGENTE')}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                    whatsAppNotifyPriority === 'URGENTE'
                      ? 'border-red-500 bg-red-500/20 text-red-300'
                      : 'border-slate-600 text-slate-300 hover:border-slate-500'
                  }`}
                >
                  URGENTE
                </button>
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-xs text-slate-400 mb-1">Observação (editável)</label>
              <textarea
                value={whatsAppNotifyObservation}
                onChange={(e) => setWhatsAppNotifyObservation(e.target.value)}
                rows={3}
                placeholder="Adicione detalhes que serão enviados na mensagem..."
                className="w-full bg-black/20 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-emerald-500 resize-y"
              />
            </div>

            <div className="rounded-lg border border-slate-700 bg-black/20 p-3 mb-5">
              <p className="text-xs text-slate-400 mb-1">Prévia da mensagem</p>
              <p className="text-sm text-slate-200 whitespace-pre-line">
                {whatsAppNotifyMembers[0] ? buildWhatsAppNotifyMessage(whatsAppNotifyMembers[0]) : ''}
              </p>
            </div>

            <div className="flex justify-end gap-2">
              <button onClick={closeWhatsAppNotifyModal} className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700/50 transition-colors">
                Cancelar
              </button>
              <button
                onClick={handleSendWhatsAppNotify}
                disabled={isSendingWhatsAppNotify || (whatsAppNotifyModal.selectedMemberInitials || []).length === 0}
                className={`px-4 py-2 rounded-lg text-white transition-colors flex items-center gap-2 ${
                  (isSendingWhatsAppNotify || (whatsAppNotifyModal.selectedMemberInitials || []).length === 0)
                    ? 'bg-slate-700 cursor-not-allowed text-slate-500'
                    : 'bg-emerald-600 hover:bg-emerald-700'
                }`}
              >
                <Send size={14} />
                {isSendingWhatsAppNotify ? 'Enviando...' : 'Enviar notificação'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {currentRoute === 'gestao_photofine' && selectedTasks.length > 0 && !activePanelTaskId ? (
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 bg-[#1c1f2b] border border-slate-600 shadow-[0_10px_40px_rgba(0,0,0,0.6)] rounded-lg flex items-center px-2 py-1.5 z-30 animate-in slide-in-from-bottom-10 fade-in duration-300">
          <div className="flex items-center gap-1 pl-4 pr-2">
            <ToolbarButton icon={<Copy size={18} />} label="Duplicar" onClick={handleDuplicateSelected} />
            <ToolbarButton icon={<Download size={18} />} label="Exportar" />
            <ToolbarButton icon={<Archive size={18} />} label={activeTab === 'arquivadas' ? 'Desarquivar' : 'Arquivar'} onClick={activeTab === 'arquivadas' ? handleUnarchiveSelected : handleArchiveSelected} />
            <ToolbarButton icon={<Trash2 size={18} />} label="Excluir" onClick={handleDeleteSelected} />
            <button onClick={openWhatsAppNotifyModal} className="flex flex-col items-center justify-center gap-1 min-w-[64px] h-[52px] rounded-md hover:bg-emerald-600/20 text-emerald-400 hover:text-emerald-300 transition-colors px-2 group" title="Enviar notificação por WhatsApp">
              <div className="group-hover:-translate-y-0.5 transition-transform"><MessageSquare size={18} /></div>
              <span className="text-[10px] font-medium">Notificar</span>
            </button>
            <ToolbarButton icon={<RefreshCw size={18} />} label="Converter" />
            <ToolbarButton icon={<ArrowRight size={18} />} label="Mover" />
            <ToolbarButton icon={<Box size={18} />} label="Apps" />
            <div className="w-px h-8 bg-slate-700 mx-2"></div>
            <button onClick={() => setSelectedTasks([])} className="p-2 ml-1 hover:bg-slate-700 rounded-md text-slate-400 hover:text-white transition-colors" title="Fechar"><X size={18} /></button>
          </div>
        </div>
      ) : null}

      {/* Painel de Notificações */}
      {isNotificationsOpen && (
        <>
          <div className="fixed inset-0 z-[90]" onClick={() => setIsNotificationsOpen(false)}></div>
          <div className="absolute top-16 right-4 w-96 max-w-[90vw] bg-[#1c1f2b] border border-slate-600 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-4 border-b border-slate-700 flex items-center justify-between" style={{ backgroundColor: 'rgba(24, 27, 38, 1)' }}>
              <div className="flex items-center gap-2">
                <Bell size={18} className="text-blue-400" />
                <h3 className="font-bold text-white">Notificações</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button onClick={markAllNotificationsAsRead} className="text-xs text-blue-400 hover:text-blue-300 transition-colors">
                    Marcar todas como lidas
                  </button>
                )}
                <button onClick={() => setIsNotificationsOpen(false)} className="p-1 hover:bg-slate-700 rounded text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Bell size={32} className="mx-auto mb-3 opacity-30" />
                  <p>Nenhuma notificação</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-700">
                  {[...notifications].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).map((notification) => {
                    const canNavigate = notification.albumId != null || notification.taskId != null;
                    const navigateTarget = notification.type === 'album_review_changes' ? 'Ver anotações do álbum'
                      : notification.type === 'album_review_approved' ? 'Ver aprovação do álbum'
                      : notification.albumId != null ? 'Abrir álbum'
                      : notification.taskId != null ? 'Abrir tarefa'
                      : null;
                    return (
                      <div
                        key={notification.id}
                        className={`p-4 transition-colors group ${canNavigate ? 'hover:bg-slate-800/50 cursor-pointer' : ''} ${!notification.read ? 'bg-blue-900/10 border-l-2 border-l-blue-500' : ''}`}
                        onClick={() => {
                          markNotificationAsRead(notification.id);
                          if (canNavigate) handleNotificationNavigate(notification);
                        }}
                        title={canNavigate ? 'Clique para abrir o destino' : 'Clique para marcar como lida'}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                            notification.type === 'task_assigned' ? 'bg-blue-500/20 text-blue-400' :
                            notification.type === 'album_assigned' ? 'bg-purple-500/20 text-purple-400' :
                            notification.type === 'album_review_changes' ? 'bg-red-500/20 text-red-300' :
                            notification.type === 'album_review_approved' ? 'bg-emerald-500/20 text-emerald-300' :
                            'bg-slate-500/20 text-slate-400'
                          }`}>
                            {notification.type === 'task_assigned' ? <CheckSquare size={16} /> :
                             notification.type === 'album_assigned' ? <LayoutGrid size={16} /> :
                             notification.type === 'album_review_changes' ? <MessageSquare size={16} /> :
                             notification.type === 'album_review_approved' ? <CheckCircle size={16} /> :
                             <Bell size={16} />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!notification.read ? 'font-semibold text-white' : 'text-slate-300'}`}>
                              {notification.title}
                            </p>
                            <p className="text-xs text-slate-400 mt-1 line-clamp-2">{notification.message}</p>
                            <div className="flex items-center justify-between gap-2 mt-2">
                              <p className="text-[10px] text-slate-500">
                                {new Date(notification.timestamp).toLocaleString('pt-BR', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                })}
                              </p>
                              {navigateTarget ? (
                                <span className="text-[10px] font-semibold text-blue-300 opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1">
                                  {navigateTarget} <ChevronRight size={11} />
                                </span>
                              ) : null}
                            </div>
                          </div>
                          {!notification.read && (
                            <div className="w-2 h-2 rounded-full bg-blue-500 shrink-0 mt-1"></div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            {notifications.length > 0 && (
              <div className="p-3 border-t border-slate-700 bg-[#181b26]">
                <button onClick={clearNotifications} className="w-full text-xs text-red-400 hover:text-red-300 transition-colors">
                  Limpar todas as notificações
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Container de Toasts */}
      <div className="fixed bottom-4 right-4 z-[200] space-y-2">
        {toasts.map((toast) => (
          <div 
            key={toast.id} 
            className="bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-lg p-4 min-w-[300px] max-w-[400px] animate-in slide-in-from-right fade-in duration-300"
          >
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                toast.type === 'success' ? 'bg-emerald-500/20 text-emerald-400' :
                toast.type === 'error' ? 'bg-red-500/20 text-red-400' :
                toast.type === 'warning' ? 'bg-yellow-500/20 text-yellow-400' :
                'bg-blue-500/20 text-blue-400'
              }`}>
                {toast.type === 'success' ? <CheckCircle size={16} /> :
                 toast.type === 'error' ? <X size={16} /> :
                 toast.type === 'warning' ? <AlertTriangle size={16} /> :
                 <Bell size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm">{toast.title}</p>
                <p className="text-xs text-slate-400 mt-1">{toast.message}</p>
              </div>
              <button 
                onClick={() => setToasts(prev => prev.filter(t => t.id !== toast.id))}
                className="text-slate-400 hover:text-white transition-colors"
              >
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de Logs do Sistema */}
      {isLogsModalOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4" onClick={() => setIsLogsModalOpen(false)}>
          <div className="border border-slate-600 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden animate-in zoom-in-95 duration-200" style={{ backgroundColor: 'rgba(28, 31, 43, 1)' }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-5 border-b border-slate-700 flex justify-between items-center" style={{ backgroundColor: 'rgba(24, 27, 38, 1)' }}>
              <div className="flex items-center gap-3">
                <Clock size={24} className="text-blue-400" />
                <div>
                  <h3 className="text-lg font-bold text-white">Logs do Sistema</h3>
                  <p className="text-xs text-slate-400">Histórico de todas as atividades</p>
                </div>
              </div>
              <button onClick={() => setIsLogsModalOpen(false)} className="p-2 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Filtros */}
            <div className="p-4 border-b border-slate-700 bg-[#1c1f2b] space-y-3">
              <div className="flex flex-wrap gap-3">
                {/* Filtro por Pessoa */}
                <div className="relative">
                  <button 
                    onClick={() => setLogsFilterPerson(logsFilterPerson ? null : 'menu')}
                    className={`px-3 py-1.5 rounded-lg border flex items-center gap-2 text-sm transition-colors ${logsFilterPerson ? 'bg-blue-600/20 border-blue-500/50 text-blue-400' : 'border-slate-600 hover:border-slate-500 text-slate-300'}`}
                  >
                    <User size={14} />
                    {logsFilterPerson || 'Todas as Pessoas'}
                    <ChevronDown size={14} />
                  </button>
                  {logsFilterPerson === 'menu' && (
                    <div className="absolute top-full left-0 mt-1 w-48 bg-[#1c1f2b] border border-slate-600 rounded-lg shadow-xl py-2 z-50">
                      <div className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer text-slate-200 text-sm" onClick={() => setLogsFilterPerson(null)}>Todas as Pessoas</div>
                      {members.map(m => (
                        <div key={m.id} className="px-3 py-1.5 hover:bg-slate-700 cursor-pointer text-slate-200 text-sm flex items-center gap-2" onClick={() => setLogsFilterPerson(m.initials)}>
                          <div className={`w-5 h-5 rounded-full ${m.color} flex items-center justify-center text-white text-[10px]`}>{m.initials}</div>
                          {m.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Filtro por Data Início */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">De:</span>
                  <input 
                    type="date" 
                    value={logsFilterDateStart}
                    onChange={(e) => setLogsFilterDateStart(e.target.value)}
                    className="bg-[#181b26] border border-slate-600 rounded px-2 py-1 text-sm text-slate-300 outline-none focus:border-blue-500"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                {/* Filtro por Data Fim */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-400">Até:</span>
                  <input 
                    type="date" 
                    value={logsFilterDateEnd}
                    onChange={(e) => setLogsFilterDateEnd(e.target.value)}
                    className="bg-[#181b26] border border-slate-600 rounded px-2 py-1 text-sm text-slate-300 outline-none focus:border-blue-500"
                    style={{ colorScheme: 'dark' }}
                  />
                </div>

                {/* Busca */}
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input 
                      type="text"
                      placeholder="Buscar nos logs..."
                      value={logsFilterSearch}
                      onChange={(e) => setLogsFilterSearch(e.target.value)}
                      className="w-full bg-[#181b26] border border-slate-600 rounded-lg pl-8 pr-3 py-1.5 text-sm text-slate-300 outline-none focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* Limpar Filtros */}
                {(logsFilterPerson || logsFilterDateStart || logsFilterDateEnd || logsFilterSearch) && (
                  <button 
                    onClick={() => {setLogsFilterPerson(null); setLogsFilterDateStart(''); setLogsFilterDateEnd(''); setLogsFilterSearch('');}}
                    className="px-3 py-1.5 text-xs text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-lg transition-colors"
                  >
                    Limpar Filtros
                  </button>
                )}
              </div>
            </div>

            {/* Lista de Logs */}
            <div className="flex-1 overflow-y-auto p-4 bg-[#11131c]" style={{ maxHeight: 'calc(90vh - 200px)' }}>
              {(() => {
                // Agregar todos os logs de todas as tarefas
                const allLogs = tasks.flatMap(task => 
                  (task.activityLog || []).map(log => ({
                    ...log,
                    taskName: log.taskName || task.elemento,
                    taskId: task.id
                  }))
                );

                // Aplicar filtros
                const filteredLogs = allLogs.filter(log => {
                  const matchPerson = logsFilterPerson && logsFilterPerson !== 'menu' ? log.authorInitials === logsFilterPerson : true;
                  const matchSearch = logsFilterSearch ? 
                    (log.details?.toLowerCase().includes(logsFilterSearch.toLowerCase()) ||
                     log.taskName?.toLowerCase().includes(logsFilterSearch.toLowerCase()) ||
                     log.field?.toLowerCase().includes(logsFilterSearch.toLowerCase())) : true;
                  
                  // Filtro de data
                  let matchDate = true;
                  if (logsFilterDateStart || logsFilterDateEnd) {
                    const logDate = log.date || new Date(log.id).toISOString().split('T')[0];
                    if (logsFilterDateStart && logDate < logsFilterDateStart) matchDate = false;
                    if (logsFilterDateEnd && logDate > logsFilterDateEnd) matchDate = false;
                  }
                  
                  return matchPerson && matchSearch && matchDate;
                }).sort((a, b) => b.id - a.id); // Ordenar por ID (mais recente primeiro)

                if (filteredLogs.length === 0) {
                  return (
                    <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                      <Clock size={48} className="mb-4 opacity-50" />
                      <p className="text-lg">Nenhum log encontrado</p>
                      <p className="text-sm mt-1">Tente ajustar os filtros</p>
                    </div>
                  );
                }

                return (
                  <div className="space-y-2">
                    {filteredLogs.map((log, index) => (
                      <div key={log.id} className={`flex items-center gap-4 p-3 rounded-lg border border-slate-700/50 hover:bg-slate-800/50 transition-colors ${index % 2 === 0 ? 'bg-[#181b26]' : 'bg-[#1c1f2b]'}`}>
                        <div className="w-24 flex flex-col text-xs text-slate-400 shrink-0">
                          <div className="flex items-center gap-1">
                            <Clock size={10} />
                            {log.time}
                          </div>
                          <div className="text-[10px] text-slate-500 mt-0.5">
                            {log.date || new Date(log.id).toLocaleDateString('pt-BR')}
                          </div>
                        </div>
                        <div className="w-10 flex justify-center shrink-0">
                          <div className="w-7 h-7 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-[10px] shadow-sm">
                            {log.authorInitials}
                          </div>
                        </div>
                        <div className="w-48 shrink-0">
                          <span className="text-sm text-slate-300 truncate block" title={log.taskName}>{log.taskName}</span>
                        </div>
                        <div className="w-28 flex items-center gap-2 text-sm text-slate-400 shrink-0">
                          {log.field === 'Created' ? <PlusCircle size={14} className="text-emerald-400"/> : 
                           log.field === 'Equipe' ? <UserPlus size={14} className="text-blue-400"/> : 
                           <Settings size={14} className="text-slate-500"/>}
                          <span className={log.field === 'Created' ? 'text-emerald-400' : log.field === 'Equipe' ? 'text-blue-400' : ''}>{log.field}</span>
                        </div>
                        <div className="flex-1 text-sm text-slate-200 truncate" title={log.details}>
                          {log.details}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-slate-700 bg-[#181b26] text-center text-xs text-slate-500">
              Total de logs: {tasks.reduce((acc, task) => acc + (task.activityLog?.length || 0), 0)}
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Status */}
      {isEditStatusModalOpen && currentUser?.role === 'admin' && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1c1f2b] border border-slate-600 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-indigo-400"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Editar Status</h3>
                  <p className="text-xs text-slate-400">Altere nome, cor e ordem no Kanban</p>
                </div>
              </div>
              <button onClick={() => setIsEditStatusModalOpen(false)} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Content - Lista de todos os status */}
            <div className="p-4">
              {/* Lista de status existentes */}
              <p className="text-[11px] text-slate-400 mb-3">Use as setas à direita de cada status para ordenar as colunas do Kanban.</p>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {Object.entries(editingStatuses).map(([originalKey, data], index, entries) => (
                  <div key={originalKey} className="p-2 bg-slate-900/50 rounded-lg">
                    <div className="flex items-center gap-2">
                      {/* Input para nome */}
                      <input
                        type="text"
                        value={data.name}
                        onChange={(e) => handleUpdateEditingStatus(originalKey, 'name', e.target.value.toUpperCase())}
                        className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50"
                        placeholder="Nome do status"
                      />
                      
                      {/* Preview da cor */}
                      <div className={`px-2 py-1 rounded text-xs font-semibold ${data.color}`}>
                        {data.name.slice(0, 12) || 'ABC'}
                      </div>
                      
                      {/* Cores rápidas */}
                      <div className="flex gap-1">
                        {[
                          'bg-red-500 text-white',
                          'bg-orange-500 text-white',
                          'bg-yellow-500 text-slate-900',
                          'bg-green-500 text-white',
                          'bg-blue-500 text-white',
                          'bg-purple-500 text-white',
                          'bg-pink-500 text-white',
                          'bg-gray-500 text-white',
                        ].map((color) => (
                          <button
                            key={color}
                            onClick={() => handleUpdateEditingStatus(originalKey, 'color', color)}
                            className={`w-4 h-4 rounded-sm ${color.split(' ')[0]} ${data.color === color ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-800' : 'hover:scale-110'} transition-all`}
                            title={color.replace('bg-', '').replace('-500', '').replace('-400', '')}
                          />
                        ))}
                      </div>
                      
                      {/* Botão excluir */}
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleMoveEditingStatus(originalKey, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded transition-colors ${index === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          title="Mover para cima"
                        >
                          <ChevronRight size={12} className="-rotate-90" />
                        </button>
                        <button
                          onClick={() => handleMoveEditingStatus(originalKey, 'down')}
                          disabled={index === entries.length - 1}
                          className={`p-1 rounded transition-colors ${index === entries.length - 1 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                          title="Mover para baixo"
                        >
                          <ChevronRight size={12} className="rotate-90" />
                        </button>
                      </div>
                      <button
                        onClick={() => handleDeleteEditingStatus(originalKey)}
                        className="p-1 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded transition-colors"
                        title="Excluir status"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                      </button>
                    </div>
                    {/* SLA Config */}
                    <div className="flex items-center gap-3 mt-2 pl-1">
                      <div className="flex items-center gap-1.5">
                        <label className="text-[11px] text-slate-400">Prazo:</label>
                        <input
                          type="number"
                          min="0"
                          max="365"
                          value={data.slaDays ?? 5}
                          onChange={(e) => handleUpdateEditingStatus(originalKey, 'slaDays', e.target.value)}
                          className="w-14 bg-slate-800 border border-slate-700 rounded px-1.5 py-1 text-xs text-white text-center focus:outline-none focus:border-indigo-500"
                        />
                        <span className="text-[10px] text-slate-500">dias</span>
                      </div>
                      <label className="flex items-center gap-1.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={data.controlaPrazo ?? true}
                          onChange={(e) => handleUpdateEditingStatus(originalKey, 'controlaPrazo', e.target.checked)}
                          className="form-checkbox h-3.5 w-3.5 rounded border-slate-600 bg-slate-800 text-indigo-500 focus:ring-indigo-500"
                        />
                        <span className="text-[11px] text-slate-400">Controla prazo</span>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Adicionar novo status */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <label className="block text-xs font-medium text-slate-400 mb-2">Adicionar Novo Status</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newStatusInput}
                    onChange={(e) => setNewStatusInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNewStatusInModal()}
                    className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500/50"
                    placeholder="Nome do novo status..."
                  />
                  <div className="flex gap-1">
                    {[
                      'bg-red-500 text-white',
                      'bg-orange-500 text-white',
                      'bg-yellow-500 text-slate-900',
                      'bg-green-500 text-white',
                      'bg-blue-500 text-white',
                      'bg-purple-500 text-white',
                      'bg-pink-500 text-white',
                      'bg-gray-500 text-white',
                    ].map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewStatusColor(color)}
                        className={`w-4 h-4 rounded-sm ${color.split(' ')[0]} ${newStatusColor === color ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-800' : 'hover:scale-110'} transition-all`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleAddNewStatusInModal}
                    disabled={!newStatusInput.trim()}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      newStatusInput.trim()
                        ? 'bg-indigo-600 hover:bg-indigo-700 text-white'
                        : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 bg-slate-800/30">
              <div className="flex gap-3">
                <div className="flex-1 text-xs text-slate-400 flex items-center">
                  Total: {Object.keys(editingStatuses).length} status
                </div>
                <button
                  onClick={() => setIsEditStatusModalOpen(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveAllStatusEdits}
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Edição de Status de Álbum */}
      {isEditAlbumStatusModalOpen && currentUser?.role === 'admin' && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[#1c1f2b] border border-slate-600 rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-700 bg-slate-800/50">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-purple-400"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">Editar Status de Álbum</h3>
                  <p className="text-xs text-slate-400">Altere nome, cor e ordem no Kanban</p>
                </div>
              </div>
              <button onClick={() => setIsEditAlbumStatusModalOpen(false)} className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors text-slate-400 hover:text-white">
                <X size={20} />
              </button>
            </div>

            {/* Content - Lista de todos os status */}
            <div className="p-4">
              {/* Lista de status existentes */}
              <p className="text-[11px] text-slate-400 mb-3">Use as setas à direita de cada status para ordenar as colunas do Kanban.</p>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {Object.entries(editingAlbumStatuses).map(([originalKey, data], index, entries) => (
                  <div key={originalKey} className="flex items-center gap-2 p-2 bg-slate-900/50 rounded-lg">
                    {/* Input para nome */}
                    <input
                      type="text"
                      value={data.name}
                      onChange={(e) => handleUpdateEditingAlbumStatus(originalKey, 'name', e.target.value.toUpperCase())}
                      className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50"
                      placeholder="Nome do status"
                    />

                    {/* Preview da cor */}
                    <div className={`px-2 py-1 rounded text-xs font-semibold ${data.color}`}>
                      {data.name.slice(0, 12) || 'ABC'}
                    </div>

                    {/* Cores rápidas */}
                    <div className="flex gap-1">
                      {[
                        'bg-red-500 text-white',
                        'bg-orange-500 text-white',
                        'bg-yellow-500 text-slate-900',
                        'bg-green-500 text-white',
                        'bg-blue-500 text-white',
                        'bg-purple-500 text-white',
                        'bg-pink-500 text-white',
                        'bg-gray-500 text-white',
                      ].map((color) => (
                        <button
                          key={color}
                          onClick={() => handleUpdateEditingAlbumStatus(originalKey, 'color', color)}
                          className={`w-4 h-4 rounded-sm ${color.split(' ')[0]} ${data.color === color ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-800' : 'hover:scale-110'} transition-all`}
                          title={color.replace('bg-', '').replace('-500', '').replace('-400', '')}
                        />
                      ))}
                    </div>

                    {/* Botão excluir */}
                    <div className="flex flex-col gap-1">
                      <button
                        onClick={() => handleMoveEditingAlbumStatus(originalKey, 'up')}
                        disabled={index === 0}
                        className={`p-1 rounded transition-colors ${index === 0 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                        title="Mover para cima"
                      >
                        <ChevronRight size={12} className="-rotate-90" />
                      </button>
                      <button
                        onClick={() => handleMoveEditingAlbumStatus(originalKey, 'down')}
                        disabled={index === entries.length - 1}
                        className={`p-1 rounded transition-colors ${index === entries.length - 1 ? 'text-slate-600 cursor-not-allowed' : 'text-slate-400 hover:text-white hover:bg-slate-700'}`}
                        title="Mover para baixo"
                      >
                        <ChevronRight size={12} className="rotate-90" />
                      </button>
                    </div>
                    <button
                      onClick={() => handleDeleteEditingAlbumStatus(originalKey)}
                      className="p-1 hover:bg-red-500/20 text-slate-400 hover:text-red-400 rounded transition-colors"
                      title="Excluir status"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 6L6 18M6 6l12 12"/></svg>
                    </button>
                  </div>
                ))}
              </div>

              {/* Adicionar novo status */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <label className="block text-xs font-medium text-slate-400 mb-2">Adicionar Novo Status</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={newAlbumStatusInput}
                    onChange={(e) => setNewAlbumStatusInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddNewAlbumStatusInModal()}
                    className="flex-1 bg-slate-800 border border-slate-700 rounded px-2 py-1.5 text-sm text-white focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-500/50"
                    placeholder="Nome do novo status..."
                  />
                  <div className="flex gap-1">
                    {[
                      'bg-red-500 text-white',
                      'bg-orange-500 text-white',
                      'bg-yellow-500 text-slate-900',
                      'bg-green-500 text-white',
                      'bg-blue-500 text-white',
                      'bg-purple-500 text-white',
                      'bg-pink-500 text-white',
                      'bg-gray-500 text-white',
                    ].map((color) => (
                      <button
                        key={color}
                        onClick={() => setNewAlbumStatusColor(color)}
                        className={`w-4 h-4 rounded-sm ${color.split(' ')[0]} ${newAlbumStatusColor === color ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-800' : 'hover:scale-110'} transition-all`}
                      />
                    ))}
                  </div>
                  <button
                    onClick={handleAddNewAlbumStatusInModal}
                    disabled={!newAlbumStatusInput.trim()}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                      newAlbumStatusInput.trim()
                        ? 'bg-purple-600 hover:bg-purple-700 text-white'
                        : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 5v14M5 12h14"/></svg>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-slate-700 bg-slate-800/30">
              <div className="flex gap-3">
                <div className="flex-1 text-xs text-slate-400 flex items-center">
                  Total: {Object.keys(editingAlbumStatuses).length} status
                </div>
                <button
                  onClick={() => setIsEditAlbumStatusModalOpen(false)}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSaveAllAlbumStatusEdits}
                  className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors text-sm font-medium"
                >
                  Aplicar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

      <FotoAiWidget
        currentUser={currentUser}
        activeRoute={currentRoute}
        activeTaskId={activePanelTaskId}
      />

      {/* Image Lightbox Modal */}
      {lightboxImage && (
        <div className="fixed inset-0 bg-black/90 z-[100] flex items-center justify-center p-4" onClick={() => setLightboxImage(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2" onClick={() => setLightboxImage(null)}>
            <X size={32} />
          </button>
          <img src={lightboxImage} alt="Visualização" className="max-w-full max-h-[90vh] object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </Suspense>
    </AppStateContext.Provider>
    </ErrorBoundary>
  );
}
